import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Button,
  VStack,
  HStack,
  Text,
  Heading,
  Alert,
  AlertIcon,
  Badge,
  SimpleGrid,
  Container,
  Spinner,
  useColorModeValue,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  useToast,
  Select,
  Checkbox,
  Divider,
} from "@chakra-ui/react";
import { useHistory, useParams } from "react-router-dom";
import ApexChart from "react-apexcharts";
import { WarningIcon } from "@chakra-ui/icons";
import {
  ActivityStatsPanel,
  DailyAggregatesTable,
  DayNavigator,
  chronological,
  chartRows,
  dayBounds,
  getMeasurementTime,
} from "./DayNavigation";

const SignificantChangeAlerts = ({ measurements }) => {
  const significantChanges = measurements?.filter(m => m.significantChange && m.hrChange && m.hrPercentChange);

  if (!significantChanges?.length) return null;

  return (
    <Box
      bg={useColorModeValue("white", "gray.700")}
      p={6}
      borderRadius="xl"
      border="1px"
      borderColor={useColorModeValue("orange.200", "orange.600")}
      width="100%"
    >
      <VStack spacing={4} align="stretch">
        <HStack>
          <WarningIcon color="orange.500" />
          <Heading size="md" color="orange.500">Significant Heart Rate Changes</Heading>
        </HStack>

        <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
          {significantChanges.map((change, index) => (
            <Box
              key={`${change.id}-${change.date}`}
              p={4}
              bg={useColorModeValue("orange.50", "orange.900")}
              borderRadius="md"
              border="1px"
              borderColor={useColorModeValue("orange.200", "orange.600")}
            >
              <VStack align="start" spacing={2}>
                <Text fontSize="sm" color="gray.500">
                  {new Date(change.date).toLocaleString()}
                </Text>
                <HStack spacing={4}>
                  <VStack align="start" spacing={0}>
                    <Text fontSize="sm" color="gray.600">Heart Rate</Text>
                    <Text fontSize="lg" fontWeight="bold">{change.hr} BPM</Text>
                  </VStack>
                  <VStack align="start" spacing={0}>
                    <Text fontSize="sm" color="gray.600">Change</Text>
                    <Text
                      fontSize="lg"
                      fontWeight="bold"
                      color={change.hrChange > 0 ? "red.500" : "blue.500"}
                    >
                      {change.hrChange > 0 ? "+" : ""}{change.hrChange.toFixed(1)} BPM
                    </Text>
                  </VStack>
                </HStack>
                <Text fontSize="sm" color="gray.600">
                  {Math.abs(change.hrPercentChange)}% {change.hrPercentChange > 0 ? "increase" : "decrease"}
                </Text>
                {change.glucoseCGM && (
                  <Text fontSize="sm" color="gray.600">
                    Glucose: {change.glucoseCGM.toFixed(1)} mg/dL
                  </Text>
                )}
              </VStack>
            </Box>
          ))}
        </SimpleGrid>
      </VStack>
    </Box>
  );
};

const TIRPieChart = ({ tirStats, timeFrame }) => {
  const borderColor = useColorModeValue("gray.200", "gray.600");
  const bgColor = useColorModeValue("white", "gray.700");
  const textColor = useColorModeValue("gray.600", "gray.300");

  // TIR ranges based on the Cleveland Clinic guidelines
  const tirRanges = [
    { label: "> 250 mg/dL", target: "< 5%", value: tirStats.severe_high || 0, color: "#FF6B35" },
    { label: "> 180 mg/dL", target: "< 25%", value: tirStats.high || 0, color: "#FFB563" },
    { label: "70-180 mg/dL", target: "> 70%", value: tirStats.target || 0, color: "#4CAF50" },
    { label: "69-54 mg/dL", target: "< 4%", value: tirStats.low || 0, color: "#FF6B35" },
    { label: "< 54 mg/dL", target: "< 1%", value: tirStats.severe_low || 0, color: "#DC3545" }
  ];

  const chartData = {
    series: tirRanges.map(range => parseFloat(range.value) || 0),
    options: {
      chart: {
        type: 'donut',
        toolbar: {
          show: false
        }
      },
      labels: tirRanges.map(range => range.label),
      colors: tirRanges.map(range => range.color),
      legend: {
        show: false
      },
      plotOptions: {
        pie: {
          donut: {
            size: '75%',
            labels: {
              show: true,
              name: {
                show: true,
                fontSize: '14px',
                offsetY: 0
              },
              value: {
                show: true,
                fontSize: '20px',
                fontWeight: 600,
                formatter: function (val) {
                  const numVal = parseFloat(val);
                  return !isNaN(numVal) ? numVal.toFixed(1) + '%' : '0%';
                }
              },
              total: {
                show: true,
                label: 'Total Time',
                formatter: function () {
                  const duration = parseFloat(tirStats.totalDurationMinutes) || 0;
                  return (duration / 60).toFixed(1) + 'h';
                }
              }
            }
          }
        }
      },
      dataLabels: {
        enabled: true,
        formatter: function (val) {
          const numVal = parseFloat(val);
          return !isNaN(numVal) ? numVal.toFixed(1) + '%' : '0%';
        },
        style: {
          fontSize: '12px',
          fontWeight: 'bold',
          colors: ['#fff']
        },
        dropShadow: {
          enabled: false
        }
      },
      tooltip: {
        y: {
          formatter: function (val) {
            const numVal = parseFloat(val);
            return !isNaN(numVal) ? numVal.toFixed(1) + '%' : '0%';
          }
        }
      },
      stroke: {
        width: 2,
        colors: [bgColor]
      }
    }
  };

  return (
    <Box
      bg={bgColor}
      p={6}
      borderRadius="xl"
      border="1px"
      borderColor={borderColor}
      width="100%"
    >
      <VStack spacing={6} align="stretch">
        <HStack justify="space-between">
          <Heading size="md">Time in Range (TIR)</Heading>
          <Text fontSize="sm" color="gray.500">{timeFrame}</Text>
        </HStack>

        {/* Main Chart */}
        <Box height="300px">
          <ApexChart
            options={chartData.options}
            series={chartData.series}
            type="donut"
            height="100%"
          />
        </Box>

        {/* Ranges and Values */}
        <SimpleGrid columns={5} spacing={4}>
          {tirRanges.map((range, index) => (
            <Box key={index}>
              <HStack spacing={2} mb={2}>
                <Box w="3px" h="16px" bg={range.color} />
                <Text fontSize="sm">{range.label}</Text>
              </HStack>
              <Text fontSize="xs" color="gray.500">Target: {range.target}</Text>
              <Text fontSize="md" fontWeight="semibold">{(parseFloat(range.value) || 0).toFixed(1)}%</Text>
            </Box>
          ))}
        </SimpleGrid>

        {/* Summary Stats */}
        <SimpleGrid columns={3} spacing={4} pt={4}>
          <Box>
            <Text color="gray.600" mb={1}>Total Duration</Text>
            <Text fontSize="2xl" fontWeight="semibold">{(tirStats.totalDurationMinutes / 60).toFixed(1)}h</Text>
          </Box>
          <Box>
            <Text color="gray.600" mb={1}>Valid Readings</Text>
            <Text fontSize="2xl" fontWeight="semibold">{tirStats.totalValidMeasurements}</Text>
          </Box>
          <Box>
            <Text color="gray.600" mb={1}>N/A Readings</Text>
            <Text fontSize="2xl" fontWeight="semibold">{tirStats.totalNullMeasurements}</Text>
          </Box>
        </SimpleGrid>
      </VStack>
    </Box>
  );
};

const TIRBarChart = ({ tirStats, timeFrame }) => {
  const borderColor = useColorModeValue("gray.200", "gray.600");
  const bgColor = useColorModeValue("white", "gray.700");
  const textColor = useColorModeValue("gray.600", "gray.300");

  // TIR ranges based on the Cleveland Clinic guidelines
  const tirRanges = [
    { label: "> 250 mg/dL", target: "< 5%", value: tirStats.severe_high || 0, color: "#FF6B35" },
    { label: "> 180 mg/dL", target: "< 25%", value: tirStats.high || 0, color: "#FFB563" },
    { label: "70-180 mg/dL", target: "> 70%", value: tirStats.target || 0, color: "#4CAF50" },
    { label: "69-54 mg/dL", target: "< 4%", value: tirStats.low || 0, color: "#FF6B35" },
    { label: "< 54 mg/dL", target: "< 1%", value: tirStats.severe_low || 0, color: "#DC3545" }
  ];

  const chartData = {
    series: [
      {
        name: "Time in Range",
        data: [tirStats.severe_high || 0, tirStats.high || 0, tirStats.target || 0, tirStats.low || 0, tirStats.severe_low || 0]
      }
    ],
    options: {
      chart: {
        type: 'bar',
        stacked: true,
        stackType: '100%',
        toolbar: {
          show: false
        }
      },
      plotOptions: {
        bar: {
          horizontal: true,
          barHeight: '60%',
          distributed: false
        }
      },
      colors: tirRanges.map(range => range.color),
      dataLabels: {
        enabled: true,
        formatter: function (val) {
          return val.toFixed(1) + '%';
        },
        style: {
          fontSize: '12px',
          fontWeight: 'bold',
          colors: ['#fff']
        },
        dropShadow: {
          enabled: false
        }
      },
      xaxis: {
        categories: ['Time in Range'],
        labels: {
          show: false
        },
        axisBorder: {
          show: false
        },
        axisTicks: {
          show: false
        }
      },
      yaxis: {
        labels: {
          show: false
        }
      },
      tooltip: {
        y: {
          formatter: function (val) {
            return val.toFixed(1) + '%';
          }
        }
      },
      legend: {
        show: false
      },
      grid: {
        show: false
      }
    }
  };

  return (
    <Box
      bg={bgColor}
      p={6}
      borderRadius="xl"
      border="1px"
      borderColor={borderColor}
      width="100%"
    >
      <VStack spacing={6} align="stretch">
        <HStack justify="space-between">
          <Heading size="md">Time in Range Distribution</Heading>
          <Text fontSize="sm" color="gray.500">{timeFrame}</Text>
        </HStack>

        {/* Main Chart */}
        <Box height="400px">
          <ApexChart
            options={chartData.options}
            series={chartData.series}
            type="pie"
            height="100%"
          />
        </Box>

        {/* Target Ranges */}
        <SimpleGrid columns={{ base: 2, md: 3, lg: 5 }} spacing={4}>
          {tirRanges.map((range, index) => (
            <VStack key={index} align="start" spacing={1}>
              <HStack>
                <Box w="12px" h="12px" bg={range.color} borderRadius="sm" />
                <Text fontSize="sm" fontWeight="medium">{range.label}</Text>
              </HStack>
              <HStack justify="space-between" width="100%">
                <Text fontSize="xs" color="gray.500">Target: {range.target}</Text>
                <Text fontSize="xs" fontWeight="bold">
                  Current: {range.value.toFixed(1)}%
                </Text>
              </HStack>
            </VStack>
          ))}
        </SimpleGrid>

        {/* Summary Stats */}
        <SimpleGrid columns={3} spacing={4} pt={4}>
          <Box>
            <Text fontSize="sm" fontWeight="bold">Total Duration</Text>
            <Text fontSize="lg">{(tirStats.totalDurationMinutes / 60).toFixed(1)}h</Text>
          </Box>
          <Box>
            <Text fontSize="sm" fontWeight="bold">Valid Readings</Text>
            <Text fontSize="lg">{tirStats.totalValidMeasurements}</Text>
          </Box>
          <Box>
            <Text fontSize="sm" fontWeight="bold">Invalid Readings</Text>
            <Text fontSize="lg">{tirStats.totalNullMeasurements}</Text>
          </Box>
        </SimpleGrid>
      </VStack>
    </Box>
  );
};
import {
  getAllPersons,
  getAllMeasurementsByIdentifier,
  getGlucoseMeasurements,
  authenticateVisaVita,
  isAuthenticated as isVisaVitaAuthenticated,
  createPaginationParams,
  getPaginationInfo,
  getDailyStats,
} from "api/visaVitaService";

/**
 * Mounts its children once they come near the viewport. ApexCharts are slow to draw, so a page with
 * many charts only pays for the ones that are looked at. Once shown, a chart stays mounted.
 */
const MountWhenVisible = ({ minHeight = 420, children }) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(typeof IntersectionObserver === 'undefined');

  useEffect(() => {
    if (visible || !ref.current) return undefined;
    const observer = new IntersectionObserver((entries) => {
      if (entries.some(entry => entry.isIntersecting)) {
        setVisible(true);
        observer.disconnect();
      }
    }, { rootMargin: '400px 0px' });
    observer.observe(ref.current);
    return () => observer.disconnect();
  }, [visible]);

  return <Box ref={ref} minH={visible ? undefined : `${minHeight}px`}>{visible ? children : null}</Box>;
};

const PatientDetails = () => {
  const [mainActiveTab, setMainActiveTab] = useState(0);
  const [dataActiveTab, setDataActiveTab] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [measurementData, setMeasurementData] = useState(null);
  const [error, setError] = useState(null);
  const [availablePersons, setAvailablePersons] = useState([]);
  const [loadingPersons, setLoadingPersons] = useState(false);
  const [glucoseThreshold, setGlucoseThreshold] = useState(70);
  const [selectedTimeFrame, setSelectedTimeFrame] = useState('1d');

  const timeFrameOptions = [
    { value: '1d', label: 'Last 24 Hours' },
    { value: '1w', label: 'Last Week' },
    { value: '1m', label: 'Last Month' },
    { value: 'all', label: 'All Time' }
  ];
  const [pagination, setPagination] = useState(createPaginationParams(100, 0));
  const [paginationInfo, setPaginationInfo] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [selectedCursanoMeasurements, setSelectedCursanoMeasurements] = useState([]);
  const [showCursanoCombinedChart, setShowCursanoCombinedChart] = useState(false);
  // 'latest': newest records page by page; 'day': all records of selectedDay
  const [viewMode, setViewMode] = useState('latest');
  const [selectedDay, setSelectedDay] = useState(null);
  const [dailyStats, setDailyStats] = useState([]);
  // Interval zoomed into on the activity graph ({ min, max } epoch millis), null for the whole view
  const [activityZoom, setActivityZoom] = useState(null);
  const lastZoomResetAt = useRef(0);

  const toast = useToast();
  const history = useHistory();
  const { identifier } = useParams();

  const getIdentifierFromPerson = (person) => {
    if (person.mapping && person.mapping.externalIdentifier) {
      return person.mapping.externalIdentifier;
    }

    if (person.email && person.email.includes("@")) {
      const emailPrefix = person.email.split("@")[0];
      const cleanIdentifier = emailPrefix.replace(/_@_/g, "_0_");
      return cleanIdentifier;
    }

    if (person.identifier) {
      return person.identifier;
    }
    if (person.publicToken) {
      return person.publicToken;
    }

    return person.id?.toString();
  };

  const AVAILABLE_IDENTIFIERS = availablePersons.map(getIdentifierFromPerson).filter(Boolean);

  const PATIENT_IDENTIFIER = identifier && AVAILABLE_IDENTIFIERS.includes(identifier)
    ? identifier
    : AVAILABLE_IDENTIFIERS[0];

  const CURSANO_MEASUREMENT_OPTIONS = {
    activities: {
      label: 'Activities',
      measurements: [
        { key: 'heartRate', label: 'Heart Rate', color: '#E53E3E' },
        { key: 'steps', label: 'Steps', color: '#38A169' },
        { key: 'respiration', label: 'Respiration', color: '#3182CE' },
        { key: 'spo2', label: 'SpO₂', color: '#805AD5' }
      ]
    },
    bloodPressures: {
      label: 'Blood Pressure',
      measurements: [
        { key: 'systolic', label: 'Systolic', color: '#E53E3E' },
        { key: 'diastolic', label: 'Diastolic', color: '#3182CE' }
      ]
    },
    temperatures: {
      label: 'Temperature',
      measurements: [
        { key: 'skinTemp1', label: 'Skin Temp 1', color: '#DD6B20' },
        { key: 'skinTemp2', label: 'Skin Temp 2', color: '#D69E2E' },
        { key: 'ambientTemp', label: 'Ambient Temp', color: '#805AD5' }
      ]
    },
    heartRateVariabilities: {
      label: 'HRV',
      measurements: [
        { key: 'rmssd', label: 'RMSSD', color: '#319795' },
        { key: 'pnn50', label: 'PNN50', color: '#38A169' },
        { key: 'sdnn', label: 'SDNN', color: '#3182CE' },
        { key: 'mrr', label: 'MRR', color: '#805AD5' },
        { key: 'hf', label: 'HF', color: '#D69E2E' },
        { key: 'lf', label: 'LF', color: '#E53E3E' }
      ]
    },
    emographies: {
      label: 'Emographies',
      measurements: [
        { key: 'cz', label: 'CZ', color: '#3182CE' },
        { key: 'pcz', label: 'PCZ', color: '#38A169' },
        { key: 'chz', label: 'CHZ', color: '#805AD5' },
        { key: 'cc', label: 'CC', color: '#DD6B20' },
        { key: 'stressSkin', label: 'Stress Skin', color: '#E53E3E' }
      ]
    }
  };

  // All color mode values - called consistently at top level
  const bgColor = useColorModeValue("gray.50", "gray.900");
  const borderColor = useColorModeValue("gray.200", "gray.600");
  const cardBg = useColorModeValue("white", "gray.700");
  const chartContainerBg = useColorModeValue("white", "gray.800");
  const tableHeaderBg = useColorModeValue("gray.50", "gray.700");
  const tableRowHoverBg = useColorModeValue("gray.50", "gray.700");
  const hStackBg = useColorModeValue("gray.50", "gray.700");
  const purpleGradient = useColorModeValue(
    "linear(to-r, purple.400, purple.600)",
    "linear(to-r, purple.300, purple.500)"
  );
  const chartTextColors = useColorModeValue('#2D3748', '#A0AEC0');
  const chartGridBorderColor = useColorModeValue('#E2E8F0', '#4A5568');
  const chartThemeMode = useColorModeValue('light', 'dark');

  const formatDateTime = (timestamp) => {
    if (!timestamp) return 'N/A';

    try {
      // Handle different date formats from the API
      let date;

      if (typeof timestamp === 'string') {
        if (timestamp.includes('T')) {
          date = new Date(timestamp);
        } else {
          date = new Date(parseInt(timestamp));
        }
      } else if (typeof timestamp === 'number') {
        date = new Date(timestamp);
      } else {
        return 'Invalid Date';
      }

      if (isNaN(date.getTime())) {
        return 'Invalid Date';
      }

      return date.toLocaleString([], {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      });
    } catch (error) {
      console.error('Error formatting date:', error, timestamp);
      return 'Invalid Date';
    }
  };

  const detectLowGlucoseEvents = (glucoseData, threshold) => {
    if (!glucoseData || !Array.isArray(glucoseData) || glucoseData.length === 0) {
      return [];
    }

    const events = [];
    let inLowEvent = false;
    let eventStart = null;

    glucoseData.forEach((reading, index) => {
      if (!reading || typeof reading.glucoseCGM !== 'number') {
        return;
      }

      const glucose = reading.glucoseCGM;

      if (glucose < threshold && !inLowEvent) {
        inLowEvent = true;
        eventStart = index;
      } else if (glucose >= threshold && inLowEvent) {
        inLowEvent = false;
        if (eventStart !== null && eventStart < glucoseData.length) {
          events.push({
            startIndex: eventStart,
            endIndex: index - 1,
            startValue: glucoseData[eventStart].glucoseCGM,
            endValue: glucoseData[index - 1].glucoseCGM,
            startTime: glucoseData[eventStart].formattedDate || glucoseData[eventStart].date,
            endTime: glucoseData[index - 1].formattedDate || glucoseData[index - 1].date,
            duration: index - eventStart
          });
        }
        eventStart = null;
      }
    });

    if (inLowEvent && eventStart !== null && eventStart < glucoseData.length) {
      events.push({
        startIndex: eventStart,
        endIndex: glucoseData.length - 1,
        startValue: glucoseData[eventStart].glucoseCGM,
        endValue: glucoseData[glucoseData.length - 1].glucoseCGM,
        startTime: glucoseData[eventStart].formattedDate || glucoseData[eventStart].date,
        endTime: glucoseData[glucoseData.length - 1].formattedDate || glucoseData[glucoseData.length - 1].date,
        duration: glucoseData.length - eventStart
      });
    }

    return events;
  };

  const hasData = (measurementType) => {
    if (!measurementData?.measurements) {
      // console.log(`hasData(${measurementType}): No measurements object`);
      return false;
    }

    const data = measurementData.measurements[measurementType];
    // console.log(`hasData(${measurementType}):`, data);

    if (!Array.isArray(data) || data.length === 0) {
      // console.log(`hasData(${measurementType}): Not an array or empty array`);
      return false;
    }

    if (measurementType === 'glucose' || measurementType === 'glucoseSub1' || measurementType === 'glucoseSub2') {
      const hasValidData = data.some(item => item.glucoseCGM !== null && item.glucoseCGM !== undefined);
      // console.log(`hasData(${measurementType}): Has valid glucose data:`, hasValidData);
      return hasValidData;
    }

    return true;
  };

  const getAvailableMeasurementTypes = () => {
    if (!measurementData?.measurements) return [];

    const measurementTypes = [
      { key: 'activities', label: 'Activities', color: 'blue.500' },
      { key: 'glucose', label: 'Glucose', color: 'green.500' },
      { key: 'glucoseSub1', label: 'Glucose Sub1', color: 'green.600' },
      { key: 'glucoseSub2', label: 'Glucose Sub2 & Heart Rate', color: 'green.700' },
      { key: 'bloodPressures', label: 'Blood Pressure', color: 'red.500' },
      { key: 'temperatures', label: 'Temperature', color: 'orange.500' },
      { key: 'heartRateVariabilities', label: 'Heart Rate Variability', color: 'purple.500' },
      { key: 'emographies', label: 'Emographies', color: 'teal.500' },
      { key: 'stressAcclerometer', label: 'Stress Accelerometer', color: 'cyan.500' },
      { key: 'StressAnnotation', label: 'Stress Annotation', color: 'pink.500' },
      { key: 'stressBioz', label: 'Stress Bioz', color: 'yellow.500' },
      { key: 'stressHeatFlux', label: 'Stress Heat Flux', color: 'indigo.500' },
      { key: 'stresPPG2', label: 'Stress PPG2', color: 'gray.500' },
      { key: 'stressPPG3', label: 'Stress PPG3', color: 'brown.500' },
      { key: 'stressRRInterval', label: 'Stress RR Interval', color: 'orange.600' },
      { key: 'StressSelfReport', label: 'Stress Self Report', color: 'purple.600' },
    ];

    return measurementTypes.filter(type => hasData(type.key));
  };

  const handlePatientChange = (event) => {
    const newIdentifier = event.target.value;
    history.push(`/admin/patient-details/${newIdentifier}`);
  };

  const handleLoadMore = async () => {
    if (!paginationInfo?.hasMore) return;

    const newPagination = {
      max: pagination.max,
      offset: pagination.offset + pagination.max
    };

    setPagination(newPagination);
    await fetchMeasurements({ pagination: newPagination, append: true });
  };

  const handleChangePageSize = async (newMax) => {
    const newPagination = {
      max: parseInt(newMax),
      offset: 0
    };

    setPagination(newPagination);
    await fetchMeasurements({ pagination: newPagination });
  };

  // Days with data, oldest first
  const availableDays = dailyStats.map(day => day.date);

  // Page size used for a single day, so that a whole day of minute-by-minute data fits in one page
  const DAY_VIEW_PAGE_SIZE = 2000;
  const MAX_TABLE_ROWS = 500;

  const handleSelectDay = async (day) => {
    if (!day) return;
    const dayPagination = { max: Math.max(pagination.max, DAY_VIEW_PAGE_SIZE), offset: 0 };
    setViewMode('day');
    setSelectedDay(day);
    setActivityZoom(null);
    setPagination(dayPagination);
    await fetchMeasurements({ pagination: dayPagination, day });
  };

  const handleShowLatest = async () => {
    const latestPagination = createPaginationParams(100, 0);
    setViewMode('latest');
    setActivityZoom(null);
    setPagination(latestPagination);
    await fetchMeasurements({ pagination: latestPagination, day: null });
  };

  const fetchDailyStats = async () => {
    if (!PATIENT_IDENTIFIER) return;
    try {
      setDailyStats(await getDailyStats(PATIENT_IDENTIFIER));
    } catch (error) {
      console.error('Error fetching daily statistics:', error);
      setDailyStats([]);
    }
  };

  const prepareActivityChartData = () => {
    if (!hasData('activities')) return { series: [], categories: [] };

    const activities = measurementData.measurements.activities;
    // Points are plotted on a datetime axis, oldest to newest, so gaps in the data stay visible
    const dataSlice = chartRows(activities)
      .filter(activity => !Number.isNaN(getMeasurementTime(activity)));
    const point = (activity, value) => ({ x: getMeasurementTime(activity), y: value });

    const heartRateData = dataSlice.map(activity => {
      const bpm = activity.bpm || activity.heartRate || 0;
      return point(activity, bpm > 0 ? bpm : null);
    });

    const stepsData = dataSlice.map(activity => {
      const steps = activity.lastSteps || activity.steps || 0;
      return point(activity, steps);
    });

    // For Corsano , add additional metrics
    const respirationData = dataSlice.map(activity => {
      const respRate = activity.respirationRate || 0;
      return point(activity, respRate > 0 ? respRate : null);
    });

    const spo2Data = dataSlice.map(activity => {
      const spo2 = activity.spo2 || 0;
      return point(activity, spo2 > 0 ? spo2 : null);
    });

    // Only counted for the "Chart data points" label; the x axis uses the point times
    const categories = dataSlice.map(getMeasurementTime);

    const hasCoranoData = dataSlice.some(activity => activity.respirationRate || activity.spo2);

    if (hasCoranoData) {
      return {
        series: [
          {
            name: 'Heart Rate (BPM)',
            data: heartRateData,
            type: 'line',
            yAxisIndex: 0
          },
          {
            name: 'Steps',
            data: stepsData,
            type: 'line',
            yAxisIndex: 1
          },
          {
            name: 'Respiration Rate',
            data: respirationData,
            type: 'line',
            yAxisIndex: 2
          },
          {
            name: 'SpO2 (%)',
            data: spo2Data,
            type: 'line',
            yAxisIndex: 3
          }
        ],
        categories
      };
    }

    return {
      series: [
        {
          name: 'Heart Rate (BPM)',
          data: heartRateData,
          type: 'line',
          yAxisIndex: 0
        },
        {
          name: 'Steps',
          data: stepsData,
          type: 'line',
          yAxisIndex: 1
        }
      ],
      categories
    };
  };

  const prepareGlucoseChartData = () => {
    if (!hasData('glucose')) return { series: [], categories: [], events: [], annotations: [] };

    try {
      const glucose = measurementData.measurements.glucose;
      const dataSlice = chartRows(glucose);

      const glucoseData = dataSlice.map(item => item.glucoseCGM !== null && item.glucoseCGM !== undefined ? item.glucoseCGM : null);
      const heartRateData = dataSlice.map(item => {
        const hr = item.hr || 0;
        return hr > 0 ? hr : null;
      });
      const thresholdLine = new Array(dataSlice.length).fill(glucoseThreshold);

      const categories = dataSlice.map(item =>
        new Date(item.formattedDate || item.date).toLocaleDateString([], {
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit'
        })
      );

      // Detect low glucose events
      const events = detectLowGlucoseEvents(dataSlice, glucoseThreshold);

      // Create annotations for low glucose events
      const annotations = events.map((event, index) => ({
        x: categories[event.startIndex],
        borderColor: '#E53E3E',
        label: {
          borderColor: '#E53E3E',
          style: {
            color: '#fff',
            background: '#E53E3E',
          },
          text: `Low Event ${index + 1}`
        }
      }));

      return {
        series: [
          {
            name: 'Glucose (mg/dL)',
            data: glucoseData,
            type: 'line',
            yAxisIndex: 0
          },
          {
            name: 'Heart Rate (BPM)',
            data: heartRateData,
            type: 'line',
            yAxisIndex: 1
          },
          {
            name: `Threshold (${glucoseThreshold} mg/dL)`,
            data: thresholdLine,
            type: 'line',
            yAxisIndex: 0
          }
        ],
        categories,
        events,
        annotations
      };
    } catch (error) {
      console.error('Error preparing glucose chart data:', error);
      return { series: [], categories: [], events: [], annotations: [] };
    }
  };

  const prepareGlucoseSub1ChartData = () => {
    if (!hasData('glucoseSub1')) return { series: [], categories: [] };

    const glucoseSub1 = measurementData.measurements.glucoseSub1;
    const dataSlice = chartRows(glucoseSub1);

    const glucoseData = dataSlice.map(item => item.glucoseCGM !== null && item.glucoseCGM !== undefined ? item.glucoseCGM : null);

    const categories = dataSlice.map(item =>
      new Date(item.formattedDate || item.date).toLocaleDateString([], {
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      })
    );

    return {
      series: [
        {
          name: 'Glucose Sub1 (mg/dL)',
          data: glucoseData,
          type: 'line'
        }
      ],
      categories
    };
  };

  const prepareGlucoseSub2ChartData = () => {
    if (!hasData('glucoseSub2')) return { series: [], categories: [] };

    const glucoseSub2 = measurementData.measurements.glucoseSub2;
    const dataSlice = chartRows(glucoseSub2);

    const glucoseData = dataSlice.map(item => item.glucoseCGM !== null && item.glucoseCGM !== undefined ? item.glucoseCGM : null);
    const heartRateData = dataSlice.map(item => {
      const hr = item.hr || 0;
      return hr > 0 ? hr : null;
    });

    const categories = dataSlice.map(item =>
      new Date(item.formattedDate || item.date).toLocaleDateString([], {
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      })
    );

    return {
      series: [
        {
          name: 'Glucose Sub2 (mg/dL)',
          data: glucoseData,
          type: 'line',
          yAxisIndex: 0
        },
        {
          name: 'Heart Rate (BPM)',
          data: heartRateData,
          type: 'line',
          yAxisIndex: 1
        }
      ],
      categories
    };
  };

  const prepareBloodPressureChartData = () => {
    if (!hasData('bloodPressures')) return { series: [], categories: [] };

    const bloodPressures = measurementData.measurements.bloodPressures;
    const dataSlice = chartRows(bloodPressures);

    const systolicData = dataSlice.map(bp => bp.systolic || 0);
    const diastolicData = dataSlice.map(bp => bp.diastolic || 0);

    const categories = dataSlice.map(bp =>
      new Date(bp.date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    );

    return {
      series: [
        {
          name: 'Systolic',
          data: systolicData,
          type: 'bar'
        },
        {
          name: 'Diastolic',
          data: diastolicData,
          type: 'bar'
        }
      ],
      categories
    };
  };

  const prepareTemperatureChartData = () => {
    if (!hasData('temperatures')) return { series: [], categories: [] };

    const temperatures = measurementData.measurements.temperatures;
    const dataSlice = chartRows(temperatures);

    // Check if this is Corsano data (has tempSk1, tempSk2, tempAmb)
    const hasCoranoData = dataSlice.some(temp => temp.tempSk1 || temp.tempSk2 || temp.tempAmb);

    const categories = dataSlice.map(temp =>
      new Date(temp.formattedDate || temp.date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    );

    if (hasCoranoData) {
      const tempSk1Data = dataSlice.map(temp => temp.tempSk1 || null);
      const tempSk2Data = dataSlice.map(temp => temp.tempSk2 || null);
      const tempAmbData = dataSlice.map(temp => temp.tempAmb || null);

      return {
        series: [
          {
            name: 'Skin Temp 1 (°C)',
            data: tempSk1Data,
            type: 'line'
          },
          {
            name: 'Skin Temp 2 (°C)',
            data: tempSk2Data,
            type: 'line'
          },
          {
            name: 'Ambient Temp (°C)',
            data: tempAmbData,
            type: 'line'
          }
        ],
        categories
      };
    }

    // Regular temperature data
    const tempData = dataSlice.map(temp => temp.temperature || temp.value || 0);

    return {
      series: [
        {
          name: 'Temperature (°C)',
          data: tempData,
          type: 'line'
        }
      ],
      categories
    };
  };

  const prepareHRVChartData = () => {
    if (!hasData('heartRateVariabilities')) return { series: [], categories: [] };

    const hrvData = measurementData.measurements.heartRateVariabilities;
    const dataSlice = chartRows(hrvData);

    const hasCoranoData = dataSlice.some(hrv =>
      hrv.mrr !== undefined || hrv.sdnn !== undefined || hrv.hf !== undefined ||
      hrv.lf !== undefined || hrv.vlf !== undefined || hrv.si !== undefined
    );

    const categories = dataSlice.map(hrv =>
      new Date(hrv.formattedDate || hrv.date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    );

    if (hasCoranoData) {
      const rmssdData = dataSlice.map(hrv => hrv.rmssd || null);
      const pnn50Data = dataSlice.map(hrv => hrv.pnn50 || null);
      const sdnnData = dataSlice.map(hrv => hrv.sdnn || null);
      const mrrData = dataSlice.map(hrv => hrv.mrr || null);
      const hfData = dataSlice.map(hrv => hrv.hf || null);
      const lfData = dataSlice.map(hrv => hrv.lf || null);

      return {
        series: [
          {
            name: 'RMSSD',
            data: rmssdData,
            type: 'line'
          },
          {
            name: 'PNN50',
            data: pnn50Data,
            type: 'line'
          },
          {
            name: 'SDNN',
            data: sdnnData,
            type: 'line'
          },
          {
            name: 'MRR',
            data: mrrData,
            type: 'line'
          },
          {
            name: 'HF',
            data: hfData,
            type: 'line'
          },
          {
            name: 'LF',
            data: lfData,
            type: 'line'
          }
        ],
        categories
      };
    }

    const rmssdData = dataSlice.map(hrv => hrv.rmssd || 0);
    const pnn50Data = dataSlice.map(hrv => hrv.pnn50 || 0);

    return {
      series: [
        {
          name: 'RMSSD',
          data: rmssdData,
          type: 'line'
        },
        {
          name: 'PNN50',
          data: pnn50Data,
          type: 'line'
        }
      ],
      categories
    };
  };

  const prepareEmographiesChartData = () => {
    if (!hasData('emographies')) return { series: [], categories: [] };

    const emographies = measurementData.measurements.emographies;
    const dataSlice = chartRows(emographies);

    const hasCoranoData = dataSlice.some(emo =>
      emo.cz !== undefined || emo.pcz !== undefined || emo.czh !== undefined ||
      emo.cc !== undefined || emo.quality !== undefined || emo.stressSkin !== undefined
    );

    const categories = dataSlice.map(emo =>
      new Date(emo.formattedDate || emo.date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    );

    if (hasCoranoData) {
      const czData = dataSlice.map(emo => emo.cz || null);
      const pczData = dataSlice.map(emo => emo.pcz || null);
      const czhData = dataSlice.map(emo => emo.czh || null);
      const ccData = dataSlice.map(emo => emo.cc || null);
      const stressSkinData = dataSlice.map(emo => emo.stressSkin || null);

      return {
        series: [
          {
            name: 'CZ',
            data: czData,
            type: 'line'
          },
          {
            name: 'PCZ',
            data: pczData,
            type: 'line'
          },
          {
            name: 'CZH',
            data: czhData,
            type: 'line'
          },
          {
            name: 'CC',
            data: ccData,
            type: 'line'
          },
          {
            name: 'Stress Skin',
            data: stressSkinData,
            type: 'line'
          }
        ],
        categories
      };
    }

    const edaData = dataSlice.map(emo => emo.eda || emo.value || 0);

    return {
      series: [
        {
          name: 'EDA',
          data: edaData,
          type: 'line'
        }
      ],
      categories
    };
  };

  // Stress measurement chart preparation functions
  const prepareStressAccelerometerChartData = () => {
    if (!hasData('stressAcclerometer')) return { series: [], categories: [] };

    const stressData = measurementData.measurements.stressAcclerometer;
    const dataSlice = chartRows(stressData);

    const categories = dataSlice.map(item =>
      new Date(item.formattedDate || item.date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    );

    // Convert raw accelerometer values to g-force (assuming 16-bit resolution, ±2g range)
    const convertToG = (rawValue) => (rawValue / 32768) * 2;

    const xAxisData = dataSlice.map(item => convertToG(item.acc_x) || null);
    const yAxisData = dataSlice.map(item => convertToG(item.acc_y) || null);
    const zAxisData = dataSlice.map(item => convertToG(item.acc_z) || null);

    // Calculate magnitude
    const magnitudeData = dataSlice.map((item, index) => {
      if (xAxisData[index] === null || yAxisData[index] === null || zAxisData[index] === null) return null;
      return Math.sqrt(
        Math.pow(xAxisData[index], 2) +
        Math.pow(yAxisData[index], 2) +
        Math.pow(zAxisData[index], 2)
      );
    });

    // Add quality indicators
    const qualityData = dataSlice.map(item => item.quality || 0);

    return {
      series: [
        { name: 'X-Axis (g)', data: xAxisData, type: 'line' },
        { name: 'Y-Axis (g)', data: yAxisData, type: 'line' },
        { name: 'Z-Axis (g)', data: zAxisData, type: 'line' },
        { name: 'Magnitude (g)', data: magnitudeData, type: 'line' }
      ],
      categories,
      annotations: {
        points: qualityData.map((quality, index) => ({
          x: categories[index],
          y: magnitudeData[index],
          marker: {
            size: 4,
            fillColor: quality >= 3 ? '#38A169' : quality >= 2 ? '#ECC94B' : '#E53E3E'
          }
        }))
      }
    };
  };

  const prepareStressAnnotationChartData = () => {
    if (!hasData('StressAnnotation')) return { series: [], categories: [] };

    const stressData = measurementData.measurements.StressAnnotation;
    const dataSlice = chartRows(stressData);

    const categories = dataSlice.map(item =>
      new Date(item.formattedDate || item.date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    );

    // Group events by type
    const eventTypes = ['Rest', 'Public Speaking', 'Walking', 'Self-Report'];
    const eventSeries = eventTypes.map(type => ({
      name: type,
      data: dataSlice.map(item => item.buttonName.includes(type) ? 1 : null),
      type: 'scatter'
    }));

    return {
      series: eventSeries,
      categories,
      annotations: {
        points: dataSlice.map((item, index) => ({
          x: categories[index],
          y: 1,
          marker: {
            size: 8,
            shape: 'circle'
          },
          label: {
            text: item.comment || item.buttonName,
            style: {
              background: '#000000',
              color: '#ffffff',
              fontSize: '10px'
            }
          }
        }))
      }
    };
  };

  const prepareStressBiozChartData = () => {
    if (!hasData('stressBioz')) return { series: [], categories: [] };

    const stressData = measurementData.measurements.stressBioz;
    const dataSlice = chartRows(stressData);

    const categories = dataSlice.map(item =>
      new Date(item.formattedDate || item.date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    );

    const biozData = dataSlice.map(item => item.value || null);

    // Calculate moving average for trend
    const windowSize = 5;
    const trendData = biozData.map((value, index) => {
      if (index < windowSize - 1) return null;
      const window = biozData.slice(index - windowSize + 1, index + 1);
      const validValues = window.filter(v => v !== null);
      return validValues.length > 0 ? validValues.reduce((a, b) => a + b, 0) / validValues.length : null;
    });

    return {
      series: [
        { name: 'BioZ', data: biozData, type: 'line' },
        { name: 'Trend', data: trendData, type: 'line', dashArray: 5 }
      ],
      categories,
      yaxis: {
        min: Math.min(...biozData.filter(v => v !== null)) * 0.95,
        max: Math.max(...biozData.filter(v => v !== null)) * 1.05
      }
    };
  };

  const prepareStressHeatFluxChartData = () => {
    if (!hasData('stressHeatFlux')) return { series: [], categories: [] };

    const stressData = measurementData.measurements.stressHeatFlux;
    const dataSlice = chartRows(stressData);

    const categories = dataSlice.map(item =>
      new Date(item.formattedDate || item.date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    );

    const heatFluxData = dataSlice.map(item => item.heatFlux || null);
    const skinTempData = dataSlice.map(item => item.skinTemp || null);
    const cbtData = dataSlice.map(item => item.cbt || null);

    // Reference ranges
    const normalSkinTempRange = { min: 32, max: 37 };
    const normalCBTRange = { min: 36.5, max: 37.5 };

    return {
      series: [
        { name: 'Heat Flux (W/m²)', data: heatFluxData, type: 'line', yAxisIndex: 0 },
        { name: 'Skin Temp (°C)', data: skinTempData, type: 'line', yAxisIndex: 1 },
        { name: 'Core Body Temp (°C)', data: cbtData, type: 'line', yAxisIndex: 1 }
      ],
      categories,
      yaxis: [
        {
          title: { text: 'Heat Flux (W/m²)' },
          min: 0,
          max: Math.max(...heatFluxData.filter(v => v !== null)) * 1.1
        },
        {
          title: { text: 'Temperature (°C)' },
          min: normalSkinTempRange.min - 1,
          max: normalCBTRange.max + 1,
          opposite: true
        }
      ],
      annotations: {
        yaxis: [
          {
            y: normalSkinTempRange.min,
            y2: normalSkinTempRange.max,
            borderColor: '#48BB78',
            fillColor: '#48BB7820',
            label: { text: 'Normal Skin Temp Range' }
          },
          {
            y: normalCBTRange.min,
            y2: normalCBTRange.max,
            borderColor: '#4299E1',
            fillColor: '#4299E120',
            label: { text: 'Normal Core Body Temp Range' }
          }
        ]
      }
    };
  };

  const prepareStressPPG2ChartData = () => {
    if (!hasData('stresPPG2')) return { series: [], categories: [] };

    const stressData = measurementData.measurements.stresPPG2;
    const dataSlice = chartRows(stressData);

    const categories = dataSlice.map(item =>
      new Date(item.formattedDate || item.date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit', fractionalSecondDigits: 3 })
    );

    const infraredData = dataSlice.filter(item => item.color === 'infrared').map(item => item.value);
    const redData = dataSlice.filter(item => item.color === 'red').map(item => item.value);
    const greenData = dataSlice.filter(item => item.color === 'green').map(item => item.value);

    const qualityData = dataSlice.map(item => item.quality || 0);

    return {
      series: [
        { name: 'Infrared', data: infraredData, type: 'line' },
        { name: 'Red', data: redData, type: 'line' },
        { name: 'Green', data: greenData, type: 'line' }
      ],
      categories,
      annotations: {
        points: qualityData.map((quality, index) => ({
          x: categories[index],
          y: infraredData[index],
          marker: {
            size: 4,
            fillColor: quality >= 3 ? '#38A169' : quality >= 2 ? '#ECC94B' : '#E53E3E'
          }
        }))
      }
    };
  };

  const prepareStressPPG3ChartData = () => {
    if (!hasData('stressPPG3')) return { series: [], categories: [] };

    const stressData = measurementData.measurements.stressPPG3;
    const dataSlice = chartRows(stressData);

    const categories = dataSlice.map(item =>
      new Date(item.formattedDate || item.date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit', fractionalSecondDigits: 3 })
    );

    const ppgData = dataSlice.map(item => item.ppg || null);

    const convertToG = (rawValue) => (rawValue / 32768) * 2;
    const accXData = dataSlice.map(item => convertToG(item.accX));
    const accYData = dataSlice.map(item => convertToG(item.accY));
    const accZData = dataSlice.map(item => convertToG(item.accZ));

    const motionThreshold = 0.1;
    const qualityData = dataSlice.map((item, index) => {
      const totalAcc = Math.sqrt(
        Math.pow(accXData[index], 2) +
        Math.pow(accYData[index], 2) +
        Math.pow(accZData[index], 2)
      );
      return totalAcc < motionThreshold ? 3 : totalAcc < 0.2 ? 2 : 1;
    });

    return {
      series: [
        { name: 'PPG', data: ppgData, type: 'line' },
        { name: 'Acc X', data: accXData, type: 'line' },
        { name: 'Acc Y', data: accYData, type: 'line' },
        { name: 'Acc Z', data: accZData, type: 'line' }
      ],
      categories,
      annotations: {
        points: qualityData.map((quality, index) => ({
          x: categories[index],
          y: ppgData[index],
          marker: {
            size: 4,
            fillColor: quality >= 3 ? '#38A169' : quality >= 2 ? '#ECC94B' : '#E53E3E'
          }
        }))
      }
    };
  };

  const prepareStressRRIntervalChartData = () => {
    if (!hasData('stressRRInterval')) return { series: [], categories: [] };

    const stressData = measurementData.measurements.stressRRInterval;
    const dataSlice = chartRows(stressData);

    const categories = dataSlice.map(item =>
      new Date(item.formattedDate || item.date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit', fractionalSecondDigits: 3 })
    );

    const rrData = dataSlice.map(item => item.rr || null);

    const hrData = rrData.map(rr => rr ? Math.round(60000 / rr) : null);

    const calculateHRV = (window) => {
      if (window.length < 2) return null;
      const validIntervals = window.filter(v => v !== null);
      if (validIntervals.length < 2) return null;

      const differences = [];
      for (let i = 1; i < validIntervals.length; i++) {
        differences.push(Math.pow(validIntervals[i] - validIntervals[i - 1], 2));
      }
      return Math.sqrt(differences.reduce((a, b) => a + b, 0) / differences.length);
    };

    const windowSize = 10;
    const hrvData = [];
    for (let i = 0; i < rrData.length; i++) {
      if (i < windowSize - 1) {
        hrvData.push(null);
        continue;
      }
      const window = rrData.slice(i - windowSize + 1, i + 1);
      hrvData.push(calculateHRV(window));
    }

    const validRR = rrData.filter(v => v !== null);
    const q1 = validRR.sort((a, b) => a - b)[Math.floor(validRR.length * 0.25)];
    const q3 = validRR.sort((a, b) => a - b)[Math.floor(validRR.length * 0.75)];
    const iqr = q3 - q1;
    const outlierThresholdLow = q1 - 1.5 * iqr;
    const outlierThresholdHigh = q3 + 1.5 * iqr;

    const outliers = rrData.map(rr =>
      rr !== null && (rr < outlierThresholdLow || rr > outlierThresholdHigh) ? rr : null
    );

    return {
      series: [
        { name: 'RR Interval (ms)', data: rrData, type: 'line' },
        { name: 'Heart Rate (BPM)', data: hrData, type: 'line', yAxisIndex: 1 },
        { name: 'HRV (RMSSD)', data: hrvData, type: 'line', yAxisIndex: 2 },
        { name: 'Outliers', data: outliers, type: 'scatter' }
      ],
      categories,
      yaxis: [
        {
          title: { text: 'RR Interval (ms)' },
          min: Math.min(...validRR) * 0.9,
          max: Math.max(...validRR) * 1.1
        },
        {
          title: { text: 'Heart Rate (BPM)' },
          min: 40,
          max: 120,
          opposite: true
        },
        {
          title: { text: 'HRV (RMSSD)' },
          min: 0,
          max: Math.max(...hrvData.filter(v => v !== null)) * 1.1,
          opposite: true
        }
      ]
    };
  };

  const prepareStressSelfReportChartData = () => {
    if (!hasData('StressSelfReport')) return { series: [], categories: [] };

    const stressData = measurementData.measurements.StressSelfReport;
    const dataSlice = chartRows(stressData);

    const categories = dataSlice.map(item =>
      new Date(item.formattedDate || item.date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    );

    const arousalData = dataSlice.map(item => item.arousal || null);
    const pleasureData = dataSlice.map(item => item.pleasure || null);
    const dominanceData = dataSlice.map(item => item.dominance || null);

    // Calculate stress level (simplified formula)
    const stressLevelData = dataSlice.map((item, index) => {
      if (arousalData[index] === null || pleasureData[index] === null) return null;
      return (arousalData[index] * (10 - pleasureData[index])) / 10;
    });

    return {
      series: [
        { name: 'Arousal', data: arousalData, type: 'line' },
        { name: 'Pleasure', data: pleasureData, type: 'line' },
        { name: 'Dominance', data: dominanceData, type: 'line' },
        { name: 'Stress Level', data: stressLevelData, type: 'line' }
      ],
      categories,
      yaxis: {
        min: 0,
        max: 10,
        labels: {
          formatter: (val) => Math.round(val)
        }
      },
      annotations: {
        points: dataSlice.map((item, index) => ({
          x: categories[index],
          y: stressLevelData[index],
          marker: {
            size: 6,
            fillColor: stressLevelData[index] > 7 ? '#E53E3E' :
              stressLevelData[index] > 4 ? '#ECC94B' : '#38A169'
          }
        }))
      }
    };
  };

  // Helper function to get the correct total count for a measurement type
  const getMeasurementTotal = (typeKey) => {
    if (measurementData?.pagination?.totals) {
      // Handle different total key formats
      const totalKey = measurementData.pagination.totals[typeKey];
      const totalKeyWithSuffix = measurementData.pagination.totals[`${typeKey}Total`];

      if (totalKey !== undefined) {
        return totalKey || 0;
      } else if (totalKeyWithSuffix !== undefined) {
        return totalKeyWithSuffix || 0;
      }
    }

    // Fallback to current loaded data count
    const measurements = measurementData?.measurements?.[typeKey];
    return Array.isArray(measurements) ? measurements.length : 0;
  };

  const prepareMeasurementOverviewChart = () => {
    if (!measurementData?.measurements) return { series: [], labels: [], colors: [] };

    const availableTypes = getAvailableMeasurementTypes();

    const series = availableTypes.map(type => getMeasurementTotal(type.key));

    const labels = availableTypes.map(type => type.label);

    const colors = availableTypes.map(type => {
      switch (type.key) {
        case 'activities': return '#3182CE';
        case 'glucose': return '#38A169';
        case 'glucoseSub1': return '#2F855A';
        case 'glucoseSub2': return '#276749';
        case 'bloodPressures': return '#E53E3E';
        case 'temperatures': return '#DD6B20';
        case 'heartRateVariabilities': return '#9F7AEA';
        case 'emographies': return '#319795';
        case 'stressAcclerometer': return '#0BC5EA';
        case 'StressAnnotation': return '#ED64A6';
        case 'stressBioz': return '#ECC94B';
        case 'stressHeatFlux': return '#6B46C1';
        case 'stresPPG2': return '#718096';
        case 'stressPPG3': return '#A0522D';
        case 'stressRRInterval': return '#C05621';
        case 'StressSelfReport': return '#805AD5';
        default: return '#718096';
      }
    });

    return { series, labels, colors };
  };

  const fetchAllPersons = async () => {
    setLoadingPersons(true);
    try {
      const authSuccess = await ensureVisaVitaAuthentication();
      if (!authSuccess) {
        setAvailablePersons([]);
        return;
      }
      const response = await getAllPersons();

      if (response?.success && Array.isArray(response.persons)) {
        setAvailablePersons(response.persons);
        // console.log(`Successfully loaded ${response.persons.length} persons`);
      } else {
        console.warn('No persons data received:', response);
        setAvailablePersons([]);
      }
    } catch (error) {
      console.error('Error fetching persons:', error);
      setAvailablePersons([]);

      toast({
        title: "Error Loading Patients",
        description: `Failed to load patients: ${error.message}`,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoadingPersons(false);
    }
  };

  const ensureVisaVitaAuthentication = async () => {
    try {
      const username = localStorage.getItem("visaVitaUsername");
      const password = localStorage.getItem("visaVitaPassword");

      if (username && password) {
        if (!isVisaVitaAuthenticated()) {
          console.log('No valid token found, attempting to authenticate...');
          const authResult = await authenticateVisaVita(username, password);
          if (!authResult.access_token) {
            throw new Error('Authentication failed - no access token received');
          }
          console.log('Authentication successful');
        }
        return true;
      } else {
        throw new Error('No stored credentials found. Please sign in again.');
      }
    } catch (error) {
      console.error('Authentication error:', error);
      setError('Authentication failed. Please sign in again.');
      return false;
    }
  };

  /**
   * Load measurements for the current patient.
   * @param {Object} [options.pagination] - page to load, defaults to the current one
   * @param {boolean} [options.append] - add the page to the loaded data ("Load more") instead of replacing it
   * @param {string|null} [options.day] - 'YYYY-MM-DD' to load only that day; defaults to the selected day in day view
   */
  const fetchMeasurements = async ({
    pagination: paginationToUse = pagination,
    append = false,
    day = viewMode === 'day' ? selectedDay : null,
  } = {}) => {
    if (!PATIENT_IDENTIFIER) {
      setError('No patient identifier available');
      return;
    }

    if (append) {
      setLoadingMore(true);
    } else {
      setIsLoading(true);
    }
    setError(null);

    try {
      const authSuccess = await ensureVisaVitaAuthentication();
      if (!authSuccess) {
        return;
      }
      const query = day ? { ...paginationToUse, ...dayBounds(day) } : paginationToUse;
      const response = await getAllMeasurementsByIdentifier(PATIENT_IDENTIFIER, query);
      // console.log('Raw API response:', response);

      if (response?.success) {
        let processedResponse = response;

        if (response.type && response.data && Array.isArray(response.data)) {

          const measurements = {
            [response.type]: response.data
          };

          processedResponse = {
            ...response,
            measurements: measurements
          };

        } else if (response.measurements && Array.isArray(response.measurements) && (isCursanoPatient() || response.person?.publicToken === 'VISAVITA_GLUCOSE')) {

          const measurements = {
            glucose: response.measurements
          };

          processedResponse = {
            ...response,
            measurements: measurements
          };
        }

        if (append) {
          const existingMeasurements = measurementData?.measurements || {};
          const newMeasurements = processedResponse.measurements || {};

          const mergedMeasurements = {};
          Object.keys(newMeasurements).forEach(key => {
            if (existingMeasurements[key]) {
              const existingArray = Array.isArray(existingMeasurements[key]) ? existingMeasurements[key] : [];
              const newArray = Array.isArray(newMeasurements[key]) ? newMeasurements[key] : [];
              mergedMeasurements[key] = [...existingArray, ...newArray];
            } else {
              mergedMeasurements[key] = newMeasurements[key];
            }
          });

          setMeasurementData({
            ...processedResponse,
            measurements: mergedMeasurements
          });
        } else {
          setMeasurementData(processedResponse);
        }
        const paginationInfo = getPaginationInfo(processedResponse);
        setPaginationInfo(paginationInfo);

        if (!append) {
          toast({
            title: "Success",
            description: day
              ? `Loaded measurements of ${day} for patient ${PATIENT_IDENTIFIER}`
              : `Loaded ${paginationToUse.max} measurements for patient ${PATIENT_IDENTIFIER}`,
            status: "success",
            duration: 3000,
            isClosable: true,
          });
        }
      } else {
        throw new Error('Failed to fetch measurements');
      }
    } catch (error) {
      console.error('Error fetching measurements:', error);
      setError(`Failed to load measurements: ${error.message}`);

      toast({
        title: "Error",
        description: `Failed to load measurements: ${error.message}`,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      if (append) {
        setLoadingMore(false);
      } else {
        setIsLoading(false);
      }
    }
  };

  useEffect(() => {
    const initializeData = async () => {
      const username = localStorage.getItem("visaVitaUsername");
      const password = localStorage.getItem("visaVitaPassword");

      await fetchAllPersons();
    };
    initializeData();
  }, []);

  useEffect(() => {
    if (PATIENT_IDENTIFIER && availablePersons.length > 0) {
      // A new patient starts on their latest records
      const latestPagination = createPaginationParams(100, 0);
      setViewMode('latest');
      setSelectedDay(null);
      setActivityZoom(null);
      setPagination(latestPagination);
      fetchMeasurements({ pagination: latestPagination, day: null });
      fetchDailyStats();
    }
  }, [PATIENT_IDENTIFIER, availablePersons.length]);

  useEffect(() => {
  }, [pagination]);

  const renderChart = (type) => {
    let chartData, title, color;

    switch (type.key) {
      case 'activities':
        chartData = prepareActivityChartData();
        title = chartData.series.length === 4 ? 'Activities - Heart Rate, Steps, Respiration & SpO2' : 'Activities - Heart Rate & Steps';
        color = 'blue.500';
        break;
      case 'glucose':
        chartData = prepareGlucoseChartData();
        title = chartData.series.length === 3 ? 'Glucose Levels & Heart Rate' : 'Glucose Levels';
        color = 'green.500';
        break;
      case 'glucoseSub1':
        chartData = prepareGlucoseSub1ChartData();
        title = 'Glucose Sub1 Levels';
        color = 'green.600';
        break;
      case 'glucoseSub2':
        chartData = prepareGlucoseSub2ChartData();
        title = 'Glucose Sub2 Levels & Heart Rate';
        color = 'green.700';
        break;
      case 'bloodPressures':
        chartData = prepareBloodPressureChartData();
        title = 'Blood Pressure';
        color = 'red.500';
        break;
      case 'temperatures':
        chartData = prepareTemperatureChartData();
        title = chartData.series.length === 3 ? 'Temperature - Skin 1, Skin 2 & Ambient' : 'Temperature';
        color = 'orange.500';
        break;
      case 'heartRateVariabilities':
        chartData = prepareHRVChartData();
        title = chartData.series.length === 6 ? 'HRV - RMSSD, PNN50, SDNN, MRR, HF & LF' : 'Heart Rate Variability';
        color = 'purple.500';
        break;
      case 'emographies':
        chartData = prepareEmographiesChartData();
        title = chartData.series.length === 5 ? 'Emographies - CZ, PCZ, CZH, CC & Stress Skin' : 'Emographies';
        color = 'teal.500';
        break;
      case 'stressAcclerometer':
        chartData = prepareStressAccelerometerChartData();
        title = 'Stress Accelerometer';
        color = 'cyan.500';
        break;
      case 'StressAnnotation':
        chartData = prepareStressAnnotationChartData();
        title = 'Stress Annotation Timeline';
        color = 'pink.500';
        break;
      case 'stressBioz':
        chartData = prepareStressBiozChartData();
        title = 'Stress BioZ';
        color = 'yellow.500';
        break;
      case 'stressHeatFlux':
        chartData = prepareStressHeatFluxChartData();
        title = 'Stress Heat Flux & Temperature';
        color = 'purple.500';
        break;
      case 'stresPPG2':
        chartData = prepareStressPPG2ChartData();
        title = 'Stress PPG2 Channels';
        color = 'gray.500';
        break;
      case 'stressPPG3':
        chartData = prepareStressPPG3ChartData();
        title = 'Stress PPG3 & Motion';
        color = 'brown.500';
        break;
      case 'stressRRInterval':
        chartData = prepareStressRRIntervalChartData();
        title = 'Stress RR Interval & HRV';
        color = 'orange.600';
        break;
      case 'StressSelfReport':
        chartData = prepareStressSelfReportChartData();
        title = 'Stress Self Report';
        color = 'purple.600';
        break;
      default:
        return null;
    }

    if (!chartData.series.length) return null;

    return (
      <Box
        key={type.key}
        bg={chartContainerBg}
        shadow="sm"
        p={6}
        borderRadius="lg"
        border="1px"
        borderColor={borderColor}
      >
        <HStack justify="space-between" align="center" mb={4}>
          <Text fontSize="lg" fontWeight="bold" color={color}>
            {title}
          </Text>
          {type.key === 'glucose' && (
            <HStack spacing={4}>
              <HStack spacing={2}>
                <Text fontSize="sm" color="gray.600">Threshold:</Text>
                <Select
                  size="sm"
                  width="80px"
                  value={glucoseThreshold}
                  onChange={async (e) => {
                    const newThreshold = Number(e.target.value);
                    setGlucoseThreshold(newThreshold);

                    try {
                      const response = await getGlucoseMeasurements(identifier, {
                        significantField: 'hr',
                        threshold: newThreshold
                      }, {
                        max: pagination.max,
                        offset: pagination.offset,
                        ...(viewMode === 'day' && selectedDay ? dayBounds(selectedDay) : {})
                      });

                      if (response?.success) {
                        setMeasurementData({
                          ...response,
                          measurements: {
                            ...measurementData?.measurements,
                            glucose: response.measurements
                          }
                        });
                      }
                    } catch (error) {
                      console.error('Error fetching glucose data:', error);
                      toast({
                        title: "Error",
                        description: `Failed to fetch glucose measurements: ${error.message}`,
                        status: "error",
                        duration: 5000,
                        isClosable: true,
                      });
                    }
                  }}
                >
                  <option value={65}>65</option>
                  <option value={70}>70</option>
                  <option value={75}>75</option>
                  <option value={80}>80</option>
                </Select>
                <Text fontSize="sm" color="gray.600">mg/dL</Text>
              </HStack>
              <HStack spacing={2}>
                <Text fontSize="sm" color="gray.600">Time Frame:</Text>
                <Select
                  size="sm"
                  width="140px"
                  value={selectedTimeFrame}
                  onChange={(e) => setSelectedTimeFrame(e.target.value)}
                >
                  {timeFrameOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Select>
              </HStack>
            </HStack>
          )}
        </HStack>
        <ApexChart
          options={{
            chart: {
              type: type.key === 'bloodPressures' ? 'bar' : 'line',
              height: 300,
              background: 'transparent',
              toolbar: { show: true },
              animations: { enabled: false },
              ...(type.key === 'bloodPressures' ? { stacked: true } : {}),
              // The zoomed interval drives the statistics under the graph
              ...(type.key === 'activities' ? {
                zoom: { enabled: true, type: 'x' },
                events: {
                  zoomed: (chartContext, { xaxis }) => {
                    // ApexCharts re-sends the old zoom right after a reset, which would undo it
                    if (Date.now() - lastZoomResetAt.current < 500) return;
                    if (xaxis?.min !== undefined && xaxis?.max !== undefined) {
                      setActivityZoom({ min: xaxis.min, max: xaxis.max });
                    }
                  },
                  beforeResetZoom: () => {
                    lastZoomResetAt.current = Date.now();
                    setActivityZoom(null);
                    return undefined;
                  }
                }
              } : {})
            },
            ...(type.key === 'glucose' && chartData.annotations && chartData.annotations.length > 0 ? {
              annotations: {
                xaxis: chartData.annotations
              }
            } : {}),
            colors: type.key === 'activities' ?
              (chartData.series.length === 4 ? ['#3182CE', '#38A169', '#E53E3E', '#DD6B20'] : ['#3182CE', '#38A169']) :
              type.key === 'glucose' ? (chartData.series.length === 3 ? ['#38A169', '#E53E3E', '#ECC94B'] : chartData.series.length === 2 ? ['#38A169', '#E53E3E'] : ['#38A169']) :
                type.key === 'glucoseSub1' ? ['#2F855A'] :
                  type.key === 'glucoseSub2' ? ['#276749', '#E53E3E'] :
                    type.key === 'bloodPressures' ? ['#E53E3E', '#C53030'] :
                      type.key === 'temperatures' ?
                        (chartData.series.length === 3 ? ['#DD6B20', '#C05621', '#3182CE'] : ['#DD6B20']) :
                        type.key === 'heartRateVariabilities' ?
                          (chartData.series.length === 6 ? ['#9F7AEA', '#319795', '#3182CE', '#38A169', '#DD6B20', '#E53E3E'] : ['#9F7AEA', '#319795']) :
                          type.key === 'emographies' ?
                            (chartData.series.length === 5 ? ['#319795', '#38A169', '#9F7AEA', '#DD6B20', '#E53E3E'] : ['#319795']) :
                            type.key === 'stressAcclerometer' ?
                              ['#3182CE', '#38A169', '#E53E3E', '#9F7AEA'] : // Blue for X, Green for Y, Red for Z, Purple for Magnitude
                              type.key === 'stressBioz' ?
                                ['#805AD5', '#553C9A'] : // Purple shades for BioZ and Trend
                                type.key === 'stressHeatFlux' ?
                                  ['#DD6B20', '#3182CE', '#E53E3E'] : // Orange for Heat Flux, Blue for Skin Temp, Red for CBT
                                  type.key === 'stressPPG2' ?
                                    ['#38B2AC', '#319795', '#2C7A7B'] : // Teal shades for PPG2 channels
                                    type.key === 'stressPPG3' ?
                                      ['#4299E1', '#3182CE', '#2B6CB0'] : // Blue shades for PPG3 channels
                                      type.key === 'stressRRInterval' ?
                                        ['#F6AD55', '#ED8936', '#DD6B20', '#9F7AEA'] : // Orange shades for RR Interval, Heart Rate, and Purple for HRV
                                        [color],
            ...(type.key === 'bloodPressures' ? {
              plotOptions: {
                bar: {
                  horizontal: false,
                  columnWidth: '60%',
                  endingShape: 'rounded'
                }
              }
            } : {}),
            stroke: {
              curve: 'smooth',
              width: type.key === 'glucose' && chartData.series.length === 3 ? [3, 2, 2] : type.key === 'glucose' && chartData.series.length === 2 ? [3, 2] : 3,
              dashArray: type.key === 'glucose' && chartData.series.length === 3 ? [0, 0, 5] : type.key === 'glucose' && chartData.series.length === 2 ? [0, 5] : 0
            },
            xaxis: type.key === 'activities' ? {
              // Continuous time axis, past to present; min/max keep the zoom across re-renders
              type: 'datetime',
              // Explicit bounds even when not zoomed: ApexCharts keeps the last min/max if they are left undefined
              min: activityZoom?.min ?? chartData.series[0]?.data?.[0]?.x,
              max: activityZoom?.max ?? chartData.series[0]?.data?.[chartData.series[0].data.length - 1]?.x,
              labels: {
                datetimeUTC: false,
                style: { colors: chartTextColors }
              }
            } : {
              categories: chartData.categories,
              // Hundreds of labels make ApexCharts layout very slow, so only a handful are drawn
              tickAmount: Math.min(chartData.categories.length, 12),
              labels: {
                style: { colors: chartTextColors },
                rotate: -45
              }
            },
            yaxis: type.key === 'activities' ?
              (chartData.series.length === 4 ? [
                {
                  seriesName: 'Heart Rate (BPM)',
                  min: 50,
                  max: 120,
                  title: {
                    text: 'Heart Rate (BPM)',
                    style: { color: '#3182CE' }
                  },
                  labels: {
                    style: { colors: chartTextColors },
                    formatter: function (val) {
                      return val ? val.toFixed(0) + ' BPM' : '0 BPM';
                    }
                  }
                },
                {
                  seriesName: 'Steps',
                  opposite: true,
                  title: {
                    text: 'Steps',
                    style: { color: '#38A169' }
                  },
                  labels: {
                    style: { colors: chartTextColors },
                    formatter: function (val) {
                      return val ? val.toFixed(0) + ' steps' : '0 steps';
                    }
                  }
                },
                {
                  seriesName: 'Respiration Rate',
                  title: {
                    text: 'Respiration Rate',
                    style: { color: '#E53E3E' }
                  },
                  labels: {
                    style: { colors: chartTextColors },
                    formatter: function (val) {
                      return val ? val.toFixed(1) + ' rpm' : '0 rpm';
                    }
                  }
                },
                {
                  seriesName: 'SpO2 (%)',
                  opposite: true,
                  min: 90,
                  max: 100,
                  title: {
                    text: 'SpO2 (%)',
                    style: { color: '#DD6B20' }
                  },
                  labels: {
                    style: { colors: chartTextColors },
                    formatter: function (val) {
                      return val ? val.toFixed(0) + '%' : '0%';
                    }
                  }
                }
              ] : [
                {
                  seriesName: 'Heart Rate (BPM)',
                  min: 50,
                  max: 120,
                  title: {
                    text: 'Heart Rate (BPM)',
                    style: { color: '#3182CE' }
                  },
                  labels: {
                    style: { colors: chartTextColors },
                    formatter: function (val) {
                      return val ? val.toFixed(0) + ' BPM' : '0 BPM';
                    }
                  }
                },
                {
                  seriesName: 'Steps',
                  opposite: true,
                  title: {
                    text: 'Steps',
                    style: { color: '#38A169' }
                  },
                  labels: {
                    style: { colors: chartTextColors },
                    formatter: function (val) {
                      return val ? val.toFixed(0) + ' steps' : '0 steps';
                    }
                  }
                }
              ]) : type.key === 'glucose' ? (chartData.series.length === 3 ? [
                {
                  seriesName: 'Glucose (mg/dL)',
                  title: {
                    text: 'Glucose (mg/dL)',
                    style: { color: '#38A169' }
                  },
                  labels: {
                    style: { colors: chartTextColors },
                    formatter: function (val) {
                      return val ? val.toFixed(1) + ' mg/dL' : '0 mg/dL';
                    }
                  }
                },
                {
                  seriesName: 'Heart Rate (BPM)',
                  opposite: true,
                  title: {
                    text: 'Heart Rate (BPM)',
                    style: { color: '#E53E3E' }
                  },
                  labels: {
                    style: { colors: chartTextColors },
                    formatter: function (val) {
                      return val ? val.toFixed(0) + ' BPM' : '0 BPM';
                    }
                  }
                }
              ] : {
                title: {
                  text: 'Glucose (mg/dL)',
                  style: { color: '#38A169' }
                },
                labels: {
                  style: { colors: chartTextColors },
                  formatter: function (val) {
                    return val ? val.toFixed(1) + ' mg/dL' : '0 mg/dL';
                  }
                }
              }) : type.key === 'glucoseSub1' ? {
                title: {
                  text: 'Glucose Sub1 (mg/dL)',
                  style: { color: '#2F855A' }
                },
                labels: {
                  style: { colors: chartTextColors },
                  formatter: function (val) {
                    return val ? val.toFixed(1) + ' mg/dL' : '0 mg/dL';
                  }
                }
              } : type.key === 'glucoseSub2' ? [
                {
                  seriesName: 'Glucose Sub2 (mg/dL)',
                  title: {
                    text: 'Glucose Sub2 (mg/dL)',
                    style: { color: '#276749' }
                  },
                  labels: {
                    style: { colors: chartTextColors },
                    formatter: function (val) {
                      return val ? val.toFixed(1) + ' mg/dL' : '0 mg/dL';
                    }
                  }
                },
                {
                  seriesName: 'Heart Rate (BPM)',
                  opposite: true,
                  title: {
                    text: 'Heart Rate (BPM)',
                    style: { color: '#E53E3E' }
                  },
                  labels: {
                    style: { colors: chartTextColors },
                    formatter: function (val) {
                      return val ? val.toFixed(0) + ' BPM' : '0 BPM';
                    }
                  }
                }
              ] : type.key === 'bloodPressures' ? {
                title: {
                  text: 'Blood Pressure (mmHg)',
                  style: { color: '#E53E3E' }
                },
                labels: {
                  style: { colors: chartTextColors },
                  formatter: function (val) {
                    return val ? val.toFixed(0) + ' mmHg' : '0 mmHg';
                  }
                }
              } : {
                labels: {
                  style: { colors: chartTextColors }
                }
              },
            legend: {
              labels: { colors: chartTextColors },
              position: 'top',
              horizontalAlign: 'center'
            },
            grid: {
              borderColor: chartGridBorderColor
            },
            theme: {
              mode: chartThemeMode
            },
            dataLabels: {
              enabled: false
            },
            tooltip: {
              shared: true,
              intersect: false,
              ...(type.key === 'activities' ? { x: { format: 'dd MMM yyyy HH:mm' } } : {}),
              ...(type.key === 'bloodPressures' ? {
                y: {
                  formatter: function (val, opts) {
                    return val + ' mmHg';
                  }
                }
              } : {})
            },
            markers: {
              // Dense series (a day of data) are drawn as lines only
              size: chartData.categories.length > 150 ? 0 : 4,
              strokeWidth: 2
            }
          }}
          series={chartData.series}
          type={type.key === 'bloodPressures' ? 'bar' : 'line'}
          height={300}
        />

        {type.key === 'activities' && (
          <ActivityStatsPanel
            activities={chronological(measurementData?.measurements?.activities).slice(-(pagination?.max || 1000))}
            zoomRange={activityZoom}
            selectedDay={viewMode === 'day' ? selectedDay : null}
            onResetZoom={() => setActivityZoom(null)}
          />
        )}

        {type.key === 'glucose' && (
          <VStack spacing={6} width="100%" mt={6}>
            {/* Alerts Section */}
            <SignificantChangeAlerts measurements={measurementData?.measurements?.glucose || []} />

            {/* TIR Chart */}
            {measurementData?.tirStats && (
              <TIRPieChart
                tirStats={measurementData.tirStats}
                timeFrame={timeFrameOptions.find(opt => opt.value === selectedTimeFrame)?.label}
              />
            )}
          </VStack>
        )}

        {/* Chart Pagination Controls */}
        <Box mt={4} p={4} bg={hStackBg} borderRadius="md">
          <HStack justify="space-between" align="center">
            <HStack spacing={4}>
              <Text fontSize="sm" color="gray.600">
                Chart data points: {chartData.categories?.length || 0}
              </Text>
              {paginationInfo && (
                <Text fontSize="sm" color="gray.600">
                  (Page size: {pagination.max})
                </Text>
              )}
            </HStack>

            <HStack spacing={3}>
              <Select
                size="sm"
                value={pagination.max}
                onChange={(e) => handleChangePageSize(e.target.value)}
                width="120px"
              >
                <option value={20}>20 per page</option>
                <option value={50}>50 per page</option>
                <option value={100}>100 per page</option>
                <option value={200}>200 per page</option>
                <option value={500}>500 per page</option>
                <option value={1000}>1000 per page</option>
                <option value={2000}>2000 per page</option>
                <option value={5000}>5000 per page</option>
              </Select>

              {paginationInfo?.hasMore && (
                <Button
                  size="sm"
                  colorScheme="blue"
                  variant="outline"
                  onClick={handleLoadMore}
                  isLoading={loadingMore}
                  loadingText="Loading..."
                >
                  Load More
                </Button>
              )}
            </HStack>
          </HStack>
        </Box>

        {/* Low Glucose Events Summary */}
        {type.key === 'glucose' && chartData.events && chartData.events.length > 0 && (
          <Box mt={4} p={4} bg={useColorModeValue("red.50", "red.900")} borderRadius="md" border="1px" borderColor={useColorModeValue("red.200", "red.600")}>
            <Text fontSize="md" fontWeight="bold" color="red.600" mb={2}>
              Low Glucose Events Detected: {chartData.events.length}
            </Text>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
              {chartData.events.map((event, index) => (
                <Box key={index} p={3} bg={useColorModeValue("white", "gray.700")} borderRadius="md" border="1px" borderColor={useColorModeValue("red.200", "red.600")}>
                  <HStack justify="space-between" mb={1}>
                    <Text fontSize="sm" fontWeight="medium">Event {index + 1}</Text>
                    <Badge colorScheme="red" size="sm">
                      {event.duration} readings
                    </Badge>
                  </HStack>
                  <VStack align="start" spacing={1}>
                    <Text fontSize="xs" color="gray.600">
                      Started: {new Date(event.startTime).toLocaleString()}
                    </Text>
                    <Text fontSize="xs" color="gray.600">
                      Ended: {new Date(event.endTime).toLocaleString()}
                    </Text>
                    <HStack spacing={2}>
                      <Text fontSize="xs">Min: <Text as="span" fontWeight="bold" color="red.500">{Math.min(event.startValue, event.endValue).toFixed(1)} mg/dL</Text></Text>
                    </HStack>
                  </VStack>
                </Box>
              ))}
            </SimpleGrid>
          </Box>
        )}
      </Box>
    );
  };

  // Helper functions for badge colors
  const getBPMColor = (bpm) => {
    if (bpm < 60) return "blue";
    if (bpm < 100) return "green";
    if (bpm < 120) return "yellow";
    return "red";
  };

  const getTemperatureColor = (temp) => {
    if (temp < 36.1) return "blue";
    if (temp < 37.2) return "green";
    if (temp < 38.0) return "yellow";
    return "red";
  };

  const getActivityTypeLabel = (type) => {
    const types = {
      1: "Light Activity",
      2: "Moderate Activity",
      3: "Vigorous Activity",
      4: "High Intensity",
      7: "Rest/Sleep"
    };
    return types[type] || "Unknown";
  };

  // Cursano measurement selection helpers
  const isCursanoPatient = () => {
    return measurementData?.person?.publicToken === 'VISAVITA_CORSANO';
  };

  const handleCursanoMeasurementToggle = (measurementKey) => {
    setSelectedCursanoMeasurements(prev => {
      if (prev.includes(measurementKey)) {
        return prev.filter(key => key !== measurementKey);
      } else {
        return [...prev, measurementKey];
      }
    });
  };

  const prepareCursanoCombinedChartData = () => {
    if (!measurementData || selectedCursanoMeasurements.length === 0) {
      return { series: [], categories: [] };
    }

    const allData = [];
    const series = [];
    const measurementMap = new Map();

    selectedCursanoMeasurements.forEach(measurementKey => {
      const [category, measurement] = measurementKey.split('.');
      const data = measurementData.measurements[category] || [];

      data.forEach(item => {
        const timestamp = item.formattedDate || item.date;
        if (!measurementMap.has(timestamp)) {
          measurementMap.set(timestamp, {});
        }

        let value = null;
        switch (measurement) {
          case 'heartRate':
            value = item.bpm || item.heartRate;
            break;
          case 'steps':
            value = item.steps || item.lastSteps;
            break;
          case 'respiration':
            value = item.respirationRate ?? item.respiration;
            break;
          case 'spo2':
            value = item.spo2;
            break;
          case 'systolic':
            value = item.systolic;
            break;
          case 'diastolic':
            value = item.diastolic;
            break;
          case 'skinTemp1':
            value = item.tempSk1 ?? item.skinTemp1;
            break;
          case 'skinTemp2':
            value = item.tempSk2 ?? item.skinTemp2;
            break;
          case 'ambientTemp':
            value = item.tempAmb ?? item.ambientTemp;
            break;
          case 'rmssd':
            value = item.rmssd;
            break;
          case 'pnn50':
            value = item.pnn50;
            break;
          case 'sdnn':
            value = item.sdnn;
            break;
          case 'mrr':
            value = item.mrr;
            break;
          case 'hf':
            value = item.hf;
            break;
          case 'lf':
            value = item.lf;
            break;
          case 'cz':
            value = item.cz;
            break;
          case 'pcz':
            value = item.pcz;
            break;
          case 'chz':
            value = item.czh ?? item.chz;
            break;
          case 'cc':
            value = item.cc;
            break;
          case 'stressSkin':
            value = item.stressSkin;
            break;
        }

        if (value !== null && value !== undefined) {
          measurementMap.get(timestamp)[measurementKey] = value;
        }
      });
    });

    // Convert to series format
    const timestamps = Array.from(measurementMap.keys()).sort();
    const categories = timestamps.map(ts => new Date(ts).toLocaleString());

    selectedCursanoMeasurements.forEach(measurementKey => {
      const [category, measurement] = measurementKey.split('.');
      const option = Object.values(CURSANO_MEASUREMENT_OPTIONS)
        .flatMap(cat => cat.measurements)
        .find(opt => opt.key === measurement);

      const data = timestamps.map(ts => {
        const dataPoint = measurementMap.get(ts);
        return dataPoint && dataPoint[measurementKey] !== undefined ? dataPoint[measurementKey] : null;
      });

      series.push({
        name: option?.label || measurement,
        data: data,
        color: option?.color || '#3182CE'
      });
    });

    return { series, categories };
  };

  // Show authentication required state
  if (isVisaVitaAuthenticated() === false && error) {
    return (
      <Container maxW="full" py={8}>
        <VStack spacing={6}>
          <Alert status="error" borderRadius="md">
            <AlertIcon />
            <VStack align="start" spacing={2}>
              <Text fontWeight="bold">Authentication Required</Text>
              <Text fontSize="sm">Please sign in to access patient data.</Text>
            </VStack>
          </Alert>
          <Button colorScheme="purple" onClick={() => history.push('/auth/signin')}>
            Sign In
          </Button>
        </VStack>
      </Container>
    );
  }

  // Show loading state for patient data
  if (isLoading && !measurementData) {
    return (
      <Container maxW="full" py={8}>
        <VStack spacing={6}>
          <Spinner size="xl" color="purple.500" thickness="4px" />
          <Text fontSize="lg" fontWeight="medium">Loading patient measurements...</Text>
          <Text fontSize="sm" color="gray.500">
            Fetching data from VisaVita API for patient: {PATIENT_IDENTIFIER}
          </Text>
        </VStack>
      </Container>
    );
  }

  // Show error state
  if (error && !measurementData) {
    return (
      <Container maxW="full" py={8}>
        <VStack spacing={6}>
          <Alert status="error" borderRadius="md">
            <AlertIcon />
            <VStack align="start" spacing={2}>
              <Text fontWeight="bold">Error Loading Patient Data</Text>
              <Text fontSize="sm">{error}</Text>
            </VStack>
          </Alert>
          <Button
            colorScheme="purple"
            onClick={() => fetchMeasurements()}
            isLoading={isLoading}
          >
            Retry Loading Data
          </Button>
        </VStack>
      </Container>
    );
  }

  return (
    <Container maxW="full" bg={bgColor} minH="100vh" py={8}>
      <VStack spacing={6} align="stretch">
        {/* Header */}
        <Box bg={cardBg} shadow="md" borderRadius="lg" p={6}>
          <HStack justify="space-between" wrap="wrap" spacing={4}>
            <VStack align="start" spacing={2}>
              <Heading size="lg" bgGradient={purpleGradient} bgClip="text">
                Patient Details Dashboard
              </Heading>
              <Text color="gray.600" fontSize="md">
                Comprehensive health measurements and analytics
              </Text>
            </VStack>
            <HStack spacing={4}>
              <Select
                placeholder="Select Patient"
                value={PATIENT_IDENTIFIER || ""}
                onChange={handlePatientChange}
                minW="250px"
                bg={cardBg}
                borderColor={borderColor}
                isDisabled={loadingPersons}
              >
                {AVAILABLE_IDENTIFIERS.map((id) => (
                  <option key={id} value={id}>
                    {id}
                  </option>
                ))}
              </Select>
              <Button
                colorScheme="purple"
                onClick={() => fetchMeasurements()}
                isLoading={isLoading}
                size="md"
              >
                Refresh Data
              </Button>
            </HStack>
          </HStack>
        </Box>

        {/* Patient Info Card */}
        {measurementData && (
          <Box bg={cardBg} shadow="md" borderRadius="lg" p={6}>
            <VStack spacing={6} align="stretch">
              <Heading size="md" color="purple.600">
                Patient Information
              </Heading>
              <TableContainer>
                <Table variant="simple" size="sm">
                  <Thead bg={tableHeaderBg}>
                    <Tr>
                      <Th fontSize="xs" fontWeight="bold" color="gray.600">Patient ID</Th>
                      <Th fontSize="xs" fontWeight="bold" color="gray.600">Email</Th>
                      <Th fontSize="xs" fontWeight="bold" color="gray.600">First Name</Th>
                      <Th fontSize="xs" fontWeight="bold" color="gray.600">Last Name</Th>
                      <Th fontSize="xs" fontWeight="bold" color="gray.600">Public Token</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    <Tr _hover={{ bg: tableRowHoverBg }}>
                      <Td py={4} fontWeight="medium" fontSize="sm">
                        {measurementData.person?.id || 'N/A'}
                      </Td>
                      <Td py={4} fontWeight="medium" fontSize="sm">
                        {measurementData.person?.email || 'N/A'}
                      </Td>
                      <Td py={4} fontWeight="medium" fontSize="sm">
                        {measurementData.person?.firstName || 'N/A'}
                      </Td>
                      <Td py={4} fontWeight="medium" fontSize="sm">
                        {measurementData.person?.lastName || 'N/A'}
                      </Td>
                      <Td py={4}>
                        <Badge
                          colorScheme={
                            measurementData.person?.publicToken === 'VISAVITA_GLUCOSE' ? 'blue' :
                              measurementData.person?.publicToken === 'VISAVITA_STRESS' ? 'red' :
                                measurementData.person?.publicToken === 'VISAVITA_CORSANO' ? 'green' : 'gray'
                          }
                          px={2}
                          py={1}
                        >
                          {measurementData.person?.publicToken || 'N/A'}
                        </Badge>
                      </Td>
                    </Tr>
                  </Tbody>
                </Table>
              </TableContainer>
            </VStack>
          </Box>
        )}

        {/* Latest records or one day at a time; applies to every graph and table below */}
        {measurementData && (
          <DayNavigator
            viewMode={viewMode}
            selectedDay={selectedDay}
            availableDays={availableDays}
            isLoading={isLoading || loadingMore}
            onSelectDay={handleSelectDay}
            onShowLatest={handleShowLatest}
          />
        )}

        {/* Min / avg / max per day of the activity data */}
        {measurementData && (
          <DailyAggregatesTable
            days={dailyStats}
            selectedDay={viewMode === 'day' ? selectedDay : null}
            onSelectDay={handleSelectDay}
          />
        )}

        {/* Measurements Overview */}
        {measurementData && getAvailableMeasurementTypes().length > 0 && (
          <Box bg={cardBg} shadow="md" borderRadius="lg" p={6}>
            <Heading size="md" mb={6} color="purple.600">
              Measurements Overview
            </Heading>
            <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={8} alignItems="start">
              <Box>
                <ApexChart
                  options={{
                    chart: {
                      type: 'donut',
                      background: 'transparent',
                      toolbar: { show: false }
                    },
                    labels: prepareMeasurementOverviewChart().labels,
                    colors: prepareMeasurementOverviewChart().colors,
                    legend: {
                      position: 'bottom',
                      labels: { colors: chartTextColors }
                    },
                    plotOptions: {
                      pie: {
                        donut: {
                          size: '70%',
                          labels: {
                            show: true,
                            name: {
                              show: true,
                              fontSize: '16px',
                              color: chartTextColors
                            },
                            value: {
                              show: true,
                              fontSize: '24px',
                              fontWeight: 'bold',
                              color: chartTextColors
                            },
                            total: {
                              show: true,
                              label: 'Total',
                              fontSize: '16px',
                              color: chartTextColors
                            }
                          }
                        }
                      }
                    },
                    responsive: [{
                      breakpoint: 480,
                      options: {
                        chart: {
                          width: 280
                        },
                        legend: {
                          position: 'bottom'
                        }
                      }
                    }],
                    theme: {
                      mode: chartThemeMode
                    }
                  }}
                  series={prepareMeasurementOverviewChart().series}
                  type="donut"
                  height={350}
                />
              </Box>
              <VStack spacing={4} align="start">
                <Text fontSize="md" color="gray.600">
                  This patient has a total of{' '}
                  <Text as="span" fontWeight="bold" color="purple.500" fontSize="lg">
                    {prepareMeasurementOverviewChart().series.reduce((a, b) => a + b, 0)}
                  </Text>{' '}
                  available measurements across {getAvailableMeasurementTypes().length} categories.
                </Text>
                <VStack spacing={3} w="full" align="stretch">
                  {getAvailableMeasurementTypes().map((type, index) => {
                    const chartData = prepareMeasurementOverviewChart();
                    const colorHex = chartData.colors[index] || '#718096';

                    return (
                      <HStack key={type.key} justify="space-between" p={3} bg={hStackBg} borderRadius="md">
                        <HStack>
                          <Box
                            w={3}
                            h={3}
                            borderRadius="full"
                            bg={colorHex}
                          />
                          <Text fontSize="sm" fontWeight="medium">{type.label}</Text>
                        </HStack>
                        <Badge colorScheme="purple" variant="subtle" px={2} py={1}>
                          {getMeasurementTotal(type.key)}
                        </Badge>
                      </HStack>
                    );
                  })}
                </VStack>
              </VStack>
            </SimpleGrid>
          </Box>
        )}

        {/* Cursano Measurement Selection Interface */}
        {isCursanoPatient() && measurementData && (
          <Box bg={cardBg} shadow="md" borderRadius="lg" p={6}>
            <VStack spacing={6} align="stretch">
              <Heading size="md" color="green.600">
                Cursano Measurement Selection
              </Heading>
              <Text fontSize="sm" color="gray.600">
                Select the measurements you want to display in a combined graph. You can select multiple measurements from different categories.
              </Text>

              <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6}>
                {Object.entries(CURSANO_MEASUREMENT_OPTIONS).map(([categoryKey, category]) => (
                  <Box key={categoryKey} p={4} border="1px" borderColor={borderColor} borderRadius="md">
                    <VStack spacing={3} align="start">
                      <Text fontSize="md" fontWeight="semibold" color="gray.700">
                        {category.label}
                      </Text>
                      <Divider />
                      <VStack spacing={2} align="start" w="full">
                        {category.measurements.map((measurement) => {
                          const measurementKey = `${categoryKey}.${measurement.key}`;
                          const isSelected = selectedCursanoMeasurements.includes(measurementKey);

                          return (
                            <Checkbox
                              key={measurementKey}
                              isChecked={isSelected}
                              onChange={() => handleCursanoMeasurementToggle(measurementKey)}
                              colorScheme="green"
                              size="sm"
                            >
                              <HStack spacing={2}>
                                <Box
                                  w={3}
                                  h={3}
                                  borderRadius="full"
                                  bg={measurement.color}
                                />
                                <Text fontSize="sm">{measurement.label}</Text>
                              </HStack>
                            </Checkbox>
                          );
                        })}
                      </VStack>
                    </VStack>
                  </Box>
                ))}
              </SimpleGrid>

              <HStack justify="space-between" pt={4}>
                <Text fontSize="sm" color="gray.600">
                  Selected: {selectedCursanoMeasurements.length} measurements
                </Text>
                <HStack spacing={3}>
                  <Button
                    size="sm"
                    colorScheme="gray"
                    variant="outline"
                    onClick={() => setSelectedCursanoMeasurements([])}
                    isDisabled={selectedCursanoMeasurements.length === 0}
                  >
                    Clear All
                  </Button>
                  <Button
                    size="sm"
                    colorScheme="green"
                    onClick={() => setShowCursanoCombinedChart(true)}
                    isDisabled={selectedCursanoMeasurements.length === 0}
                  >
                    Generate Combined Chart
                  </Button>
                </HStack>
              </HStack>
            </VStack>
          </Box>
        )}

        {/* Cursano Combined Chart */}
        {isCursanoPatient() && showCursanoCombinedChart && selectedCursanoMeasurements.length > 0 && (
          <Box bg={cardBg} shadow="md" borderRadius="lg" p={6}>
            <VStack spacing={6} align="stretch">
              <HStack justify="space-between" align="center">
                <Heading size="md" color="green.600">
                  Combined Cursano Measurements Chart
                </Heading>
                <Button
                  size="sm"
                  colorScheme="gray"
                  variant="outline"
                  onClick={() => setShowCursanoCombinedChart(false)}
                >
                  Hide Chart
                </Button>
              </HStack>

              <Box>
                <ApexChart
                  options={{
                    chart: {
                      type: 'line',
                      background: 'transparent',
                      toolbar: { show: true },
                      zoom: { enabled: true }
                    },
                    stroke: {
                      curve: 'smooth',
                      width: 2
                    },
                    xaxis: {
                      type: 'category',
                      categories: prepareCursanoCombinedChartData().categories,
                      labels: {
                        style: { colors: chartTextColors },
                        rotate: -45,
                        rotateAlways: false
                      }
                    },
                    yaxis: {
                      labels: {
                        style: { colors: chartTextColors }
                      }
                    },
                    legend: {
                      labels: { colors: chartTextColors },
                      position: 'top',
                      horizontalAlign: 'center'
                    },
                    grid: {
                      borderColor: chartGridBorderColor
                    },
                    theme: {
                      mode: chartThemeMode
                    },
                    dataLabels: {
                      enabled: false
                    },
                    tooltip: {
                      shared: true,
                      intersect: false
                    },
                    markers: {
                      size: 4,
                      strokeWidth: 2
                    }
                  }}
                  series={prepareCursanoCombinedChartData().series}
                  type="line"
                  height={400}
                />
              </Box>

              {/* Chart Pagination Controls */}
              <Box mt={4} p={4} bg={hStackBg} borderRadius="md">
                <HStack justify="space-between" align="center">
                  <HStack spacing={4}>
                    <Text fontSize="sm" color="gray.600">
                      Chart data points: {prepareCursanoCombinedChartData().categories?.length || 0}
                    </Text>
                    {paginationInfo && (
                      <Text fontSize="sm" color="gray.600">
                        (Page size: {pagination.max})
                      </Text>
                    )}
                  </HStack>

                  <HStack spacing={3}>
                    <Select
                      size="sm"
                      value={pagination.max}
                      onChange={(e) => handleChangePageSize(e.target.value)}
                      width="120px"
                    >
                      <option value={20}>20 per page</option>
                      <option value={50}>50 per page</option>
                      <option value={100}>100 per page</option>
                      <option value={200}>200 per page</option>
                      <option value={500}>500 per page</option>
                      <option value={1000}>1000 per page</option>
                      <option value={2000}>2000 per page</option>
                      <option value={5000}>5000 per page</option>
                    </Select>

                    {paginationInfo?.hasMore && (
                      <Button
                        size="sm"
                        colorScheme="green"
                        variant="outline"
                        onClick={handleLoadMore}
                        isLoading={loadingMore}
                        loadingText="Loading..."
                      >
                        Load More
                      </Button>
                    )}
                  </HStack>
                </HStack>
              </Box>
            </VStack>
          </Box>
        )}

        {/* Main Tabbed Interface */}
        <Box bg={cardBg} shadow="md" borderRadius="lg" p={6}>
          <VStack spacing={6} align="stretch">
            <Tabs index={mainActiveTab} onChange={setMainActiveTab} colorScheme="purple" size="lg" isLazy>
              <TabList>
                <Tab fontWeight="semibold">
                  Graphs
                </Tab>
                <Tab fontWeight="semibold">
                  Measurements Data
                </Tab>
              </TabList>

              <TabPanels>
                {/* Charts Tab Panel */}
                <TabPanel p={0} pt={6}>
                  <VStack spacing={6} align="stretch">
                    {getAvailableMeasurementTypes().length > 0 ? (
                      <SimpleGrid columns={1} spacing={6}>
                        {getAvailableMeasurementTypes().map(type => (
                          <Box key={`${type.key}-${pagination.max}-${pagination.offset}`}>
                            {mainActiveTab === 0 && <MountWhenVisible>{renderChart(type)}</MountWhenVisible>}
                          </Box>
                        ))}
                      </SimpleGrid>
                    ) : (
                      <Box textAlign="center" py={12}>
                        <Text fontSize="lg" color="gray.500" mb={4}>
                          {viewMode === 'day' ? 'No measurements on this day' : 'No measurement data available for this patient'}
                        </Text>
                        <Text fontSize="sm" color="gray.400">
                          {viewMode === 'day'
                            ? 'Pick another day with the arrows or the date field, or go back to the latest records.'
                            : 'All measurement arrays are empty. Please check the data source.'}
                        </Text>
                      </Box>
                    )}
                  </VStack>
                </TabPanel>

                {/* Data Tab Panel */}
                <TabPanel p={0} pt={6}>
                  <VStack spacing={6} align="stretch">
                    {getAvailableMeasurementTypes().length > 0 ? (
                      <Tabs
                        index={dataActiveTab}
                        onChange={setDataActiveTab}
                        variant="enclosed"
                        colorScheme="purple"
                        isLazy
                      >
                        <TabList px={6} pt={4}>
                          {getAvailableMeasurementTypes().map(type => (
                            <Tab key={type.key} fontWeight="medium">
                              {type.label} ({getMeasurementTotal(type.key)})
                            </Tab>
                          ))}
                        </TabList>

                        <TabPanels bg={cardBg} borderRadius="0 0 md md">
                          {getAvailableMeasurementTypes().map((type, typeIndex) => (
                            <TabPanel key={`${type.key}-panel`} px={0} pb={0}>
                              {mainActiveTab === 1 && dataActiveTab === typeIndex && renderDataTable(type)}
                            </TabPanel>
                          ))}
                        </TabPanels>
                      </Tabs>
                    ) : (
                      <Box textAlign="center" py={12}>
                        <Text fontSize="lg" color="gray.500" mb={4}>
                          {viewMode === 'day' ? 'No measurements on this day' : 'No measurement data available for this patient'}
                        </Text>
                        <Text fontSize="sm" color="gray.400">
                          {viewMode === 'day'
                            ? 'Pick another day with the arrows or the date field, or go back to the latest records.'
                            : 'All measurement arrays are empty. Please check the data source.'}
                        </Text>
                      </Box>
                    )}
                  </VStack>
                </TabPanel>
              </TabPanels>
            </Tabs>
          </VStack>
        </Box>
      </VStack>
    </Container>
  );

  // Helper function to render data tables for each measurement type
  function renderDataTable(type) {
    const loadedData = measurementData.measurements[type.key];
    // A day of sensor data is thousands of rows; drawing them all freezes the page
    const data = Array.isArray(loadedData) ? loadedData.slice(0, MAX_TABLE_ROWS) : loadedData;
    if (!data || data.length === 0) {
      return (
        <Box textAlign="center" py={8}>
          <Text color="gray.500">No {type.label.toLowerCase()} data available</Text>
        </Box>
      );
    }

    // Pagination controls component
    const PaginationControls = () => (
      <Box mt={4} p={4} bg={hStackBg} borderRadius="md">
        <HStack justify="space-between" align="center">
          <HStack spacing={4}>
            <Text fontSize="sm" color="gray.600">
              {loadedData.length > data.length
                ? `Showing the first ${data.length} of ${loadedData.length} loaded records`
                : `Showing ${data.length} records`}
            </Text>
            {paginationInfo && (
              <Text fontSize="sm" color="gray.600">
                (Page size: {pagination.max})
              </Text>
            )}
          </HStack>

          <HStack spacing={3}>
            <Select
              size="sm"
              value={pagination.max}
              onChange={(e) => handleChangePageSize(e.target.value)}
              width="120px"
            >
              <option value={20}>20 per page</option>
              <option value={50}>50 per page</option>
              <option value={100}>100 per page</option>
              <option value={200}>200 per page</option>
              <option value={500}>500 per page</option>
              <option value={1000}>1000 per page</option>
              <option value={2000}>2000 per page</option>
              <option value={5000}>5000 per page</option>
            </Select>

            {paginationInfo?.hasMore && (
              <Button
                size="sm"
                colorScheme="blue"
                variant="outline"
                onClick={handleLoadMore}
                isLoading={loadingMore}
                loadingText="Loading..."
              >
                Load More
              </Button>
            )}
          </HStack>
        </HStack>
      </Box>
    );

    // Render specific table based on measurement type
    switch (type.key) {
      case 'activities':
        // Check if this is Corsano data
        const hasCoranoActivityData = data.some(activity =>
          activity.respirationRate || activity.spo2 || activity.gaitAsymmetry || activity.gaitSpeed
        );

        if (hasCoranoActivityData) {
          return (
            <Box>
              <TableContainer>
                <Table variant="simple" size="sm">
                  <Thead bg={tableHeaderBg}>
                    <Tr>
                      <Th fontSize="xs">Time</Th>
                      <Th fontSize="xs">Heart Rate</Th>
                      <Th fontSize="xs">Steps</Th>
                      <Th fontSize="xs">Respiration</Th>
                      <Th fontSize="xs">SpO2</Th>
                      <Th fontSize="xs">Gait Speed</Th>
                      <Th fontSize="xs">Gait Asymmetry</Th>
                      <Th fontSize="xs">Battery</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {data.map((activity) => (
                      <Tr key={activity.id} _hover={{ bg: tableRowHoverBg }}>
                        <Td py={3}>{formatDateTime(activity.formattedDate || activity.date)}</Td>
                        <Td py={3}>
                          <Badge colorScheme={getBPMColor(activity.bpm)} px={2} py={1}>
                            {activity.bpm ? `${activity.bpm} BPM` : 'N/A'}
                          </Badge>
                        </Td>
                        <Td py={3}>{activity.lastSteps || 'N/A'}</Td>
                        <Td py={3}>
                          <Badge colorScheme="cyan" px={2} py={1}>
                            {activity.respirationRate ? `${activity.respirationRate.toFixed(1)}` : 'N/A'}
                          </Badge>
                        </Td>
                        <Td py={3}>
                          <Badge colorScheme="teal" px={2} py={1}>
                            {activity.spo2 ? `${activity.spo2}%` : 'N/A'}
                          </Badge>
                        </Td>
                        <Td py={3}>
                          <Badge colorScheme="orange" px={2} py={1}>
                            {activity.gaitSpeed ? `${activity.gaitSpeed} m/s` : 'N/A'}
                          </Badge>
                        </Td>
                        <Td py={3}>
                          <Badge colorScheme="purple" px={2} py={1}>
                            {activity.gaitAsymmetry ? `${activity.gaitAsymmetry}%` : 'N/A'}
                          </Badge>
                        </Td>
                        <Td py={3}>
                          <Badge colorScheme={activity.battery > 50 ? "green" : activity.battery > 20 ? "yellow" : "red"} px={2} py={1}>
                            {activity.battery ? `${activity.battery}%` : 'N/A'}
                          </Badge>
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </TableContainer>
              <PaginationControls />
            </Box>
          );
        }

        // Regular activities table
        return (
          <Box>
            <TableContainer>
              <Table variant="simple" size="sm">
                <Thead bg={tableHeaderBg}>
                  <Tr>
                    <Th fontSize="xs">Time</Th>
                    <Th fontSize="xs">Activity Type</Th>
                    <Th fontSize="xs">Heart Rate</Th>
                    <Th fontSize="xs">Steps</Th>
                    <Th fontSize="xs">Status</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {data.map((activity) => (
                    <Tr key={activity.id} _hover={{ bg: tableRowHoverBg }}>
                      <Td py={3}>{formatDateTime(activity.formattedDate || activity.date)}</Td>
                      <Td py={3}>
                        <Badge colorScheme="blue" px={2} py={1}>
                          {getActivityTypeLabel(activity.activityType)}
                        </Badge>
                      </Td>
                      <Td py={3}>
                        <Badge colorScheme={getBPMColor(activity.bpm)} px={2} py={1}>
                          {activity.bpm ? `${activity.bpm} BPM` : 'N/A'}
                        </Badge>
                      </Td>
                      <Td py={3}>{activity.lastSteps || activity.steps || 'N/A'}</Td>
                      <Td py={3}>
                        <Badge colorScheme="green" px={2} py={1}>Active</Badge>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </TableContainer>
            <PaginationControls />
          </Box>
        );

      case 'glucose':
        return (
          <Box>
            <TableContainer>
              <Table variant="simple" size="sm">
                <Thead bg={tableHeaderBg}>
                  <Tr>
                    <Th fontSize="xs">Date & Time</Th>
                    <Th fontSize="xs">Glucose (mg/dL)</Th>
                    <Th fontSize="xs">Heart Rate (BPM)</Th>
                    <Th fontSize="xs">Database</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {data.map((glucose) => (
                    <Tr key={glucose.id} _hover={{ bg: tableRowHoverBg }}>
                      <Td py={3}>
                        {new Date(glucose.date).toLocaleString([], {
                          year: 'numeric',
                          month: '2-digit',
                          day: '2-digit',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </Td>
                      <Td py={3}>
                        <Badge colorScheme="green" px={2} py={1}>
                          {glucose.glucoseCGM !== null && glucose.glucoseCGM !== undefined
                            ? `${glucose.glucoseCGM.toFixed(1)} mg/dL`
                            : 'N/A'}
                        </Badge>
                      </Td>
                      <Td py={3}>
                        <Badge
                          colorScheme={glucose.hr > 0 ? (glucose.hr < 60 ? "blue" : glucose.hr < 100 ? "green" : glucose.hr < 120 ? "yellow" : "red") : "gray"}
                          px={2}
                          py={1}
                        >
                          {glucose.hr > 0 ? `${glucose.hr} BPM` : 'N/A'}
                        </Badge>
                      </Td>
                      <Td py={3}>{glucose.database || 'N/A'}</Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </TableContainer>
            <PaginationControls />
          </Box>
        );

      case 'glucoseSub1':
        return (
          <Box>
            <TableContainer>
              <Table variant="simple" size="sm">
                <Thead bg={tableHeaderBg}>
                  <Tr>
                    <Th fontSize="xs">Date & Time</Th>
                    <Th fontSize="xs">Glucose Sub1 (mg/dL)</Th>
                    <Th fontSize="xs">Age</Th>
                    <Th fontSize="xs">Sex</Th>
                    <Th fontSize="xs">Age Group</Th>
                    <Th fontSize="xs">Database</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {data.map((glucose) => (
                    <Tr key={glucose.id} _hover={{ bg: tableRowHoverBg }}>
                      <Td py={3}>
                        {new Date(glucose.date).toLocaleString([], {
                          year: 'numeric',
                          month: '2-digit',
                          day: '2-digit',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </Td>
                      <Td py={3}>
                        <Badge colorScheme="green" px={2} py={1}>
                          {glucose.glucoseCGM !== null && glucose.glucoseCGM !== undefined
                            ? `${glucose.glucoseCGM.toFixed(1)} mg/dL`
                            : 'N/A'}
                        </Badge>
                      </Td>
                      <Td py={3}>
                        <Badge colorScheme="blue" px={2} py={1}>
                          {glucose.age || 'N/A'}
                        </Badge>
                      </Td>
                      <Td py={3}>
                        <Badge
                          colorScheme={glucose.sex === 'F' ? 'pink' : glucose.sex === 'M' ? 'blue' : 'gray'}
                          px={2}
                          py={1}
                        >
                          {glucose.sex || 'N/A'}
                        </Badge>
                      </Td>
                      <Td py={3}>
                        <Badge colorScheme="purple" px={2} py={1}>
                          {glucose.ageGroup || 'N/A'}
                        </Badge>
                      </Td>
                      <Td py={3}>{glucose.database || 'N/A'}</Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </TableContainer>
            <PaginationControls />
          </Box>
        );

      case 'glucoseSub2':
        return (
          <TableContainer>
            <Table variant="simple" size="sm">
              <Thead bg={tableHeaderBg}>
                <Tr>
                  <Th fontSize="xs">Date & Time</Th>
                  <Th fontSize="xs">Glucose Sub2 (mg/dL)</Th>
                  <Th fontSize="xs">Heart Rate (BPM)</Th>
                  <Th fontSize="xs">Database</Th>
                </Tr>
              </Thead>
              <Tbody>
                {data.map((glucose) => (
                  <Tr key={glucose.id} _hover={{ bg: tableRowHoverBg }}>
                    <Td py={3}>
                      {new Date(glucose.date).toLocaleString([], {
                        year: 'numeric',
                        month: '2-digit',
                        day: '2-digit',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </Td>
                    <Td py={3}>
                      <Badge colorScheme="green" px={2} py={1}>
                        {glucose.glucoseCGM !== null && glucose.glucoseCGM !== undefined
                          ? `${glucose.glucoseCGM.toFixed(1)} mg/dL`
                          : 'N/A'}
                      </Badge>
                    </Td>
                    <Td py={3}>
                      <Badge
                        colorScheme={glucose.hr > 0 ? (glucose.hr < 60 ? "blue" : glucose.hr < 100 ? "green" : glucose.hr < 120 ? "yellow" : "red") : "gray"}
                        px={2}
                        py={1}
                      >
                        {glucose.hr > 0 ? `${glucose.hr} BPM` : 'N/A'}
                      </Badge>
                    </Td>
                    <Td py={3}>{glucose.database || 'N/A'}</Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </TableContainer>
        );

      case 'bloodPressures':
        return (
          <Box>
            <TableContainer>
              <Table variant="simple" size="sm">
                <Thead bg={tableHeaderBg}>
                  <Tr>
                    <Th fontSize="xs">Time</Th>
                    <Th fontSize="xs">Systolic</Th>
                    <Th fontSize="xs">Diastolic</Th>
                    <Th fontSize="xs">Status</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {data.map((bp) => (
                    <Tr key={bp.id} _hover={{ bg: tableRowHoverBg }}>
                      <Td py={3}>{formatDateTime(bp.date)}</Td>
                      <Td py={3}>
                        <Badge colorScheme="red" px={2} py={1}>
                          {bp.systolic} mmHg
                        </Badge>
                      </Td>
                      <Td py={3}>
                        <Badge colorScheme="blue" px={2} py={1}>
                          {bp.diastolic} mmHg
                        </Badge>
                      </Td>
                      <Td py={3}>
                        <Badge colorScheme="green" px={2} py={1}>Normal</Badge>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </TableContainer>
            <PaginationControls />
          </Box>
        );

      case 'temperatures':
        // Check if this is Corsano data
        const hasCoranoTempData = data.some(temp =>
          temp.tempSk1 !== undefined || temp.tempSk2 !== undefined || temp.tempAmb !== undefined
        );

        if (hasCoranoTempData) {
          return (
            <Box>
              <TableContainer>
                <Table variant="simple" size="sm">
                  <Thead bg={tableHeaderBg}>
                    <Tr>
                      <Th fontSize="xs">Time</Th>
                      <Th fontSize="xs">Skin Temp 1</Th>
                      <Th fontSize="xs">Skin Temp 2</Th>
                      <Th fontSize="xs">Ambient Temp</Th>
                      <Th fontSize="xs">Status</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {data.map((temp) => (
                      <Tr key={temp.id} _hover={{ bg: tableRowHoverBg }}>
                        <Td py={3}>{formatDateTime(temp.formattedDate || temp.date)}</Td>
                        <Td py={3}>
                          <Badge colorScheme={getTemperatureColor(temp.tempSk1)} px={2} py={1}>
                            {temp.tempSk1 ? `${temp.tempSk1.toFixed(2)}°C` : 'N/A'}
                          </Badge>
                        </Td>
                        <Td py={3}>
                          <Badge colorScheme={getTemperatureColor(temp.tempSk2)} px={2} py={1}>
                            {temp.tempSk2 ? `${temp.tempSk2.toFixed(2)}°C` : 'N/A'}
                          </Badge>
                        </Td>
                        <Td py={3}>
                          <Badge colorScheme="blue" px={2} py={1}>
                            {temp.tempAmb ? `${temp.tempAmb.toFixed(2)}°C` : 'N/A'}
                          </Badge>
                        </Td>
                        <Td py={3}>
                          <Badge colorScheme="green" px={2} py={1}>
                            Normal
                          </Badge>
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </TableContainer>
              <PaginationControls />
            </Box>
          );
        }

        // Regular temperature table
        return (
          <Box>
            <TableContainer>
              <Table variant="simple" size="sm">
                <Thead bg={tableHeaderBg}>
                  <Tr>
                    <Th fontSize="xs">Time</Th>
                    <Th fontSize="xs">Temperature</Th>
                    <Th fontSize="xs">Status</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {data.map((temp) => (
                    <Tr key={temp.id} _hover={{ bg: tableRowHoverBg }}>
                      <Td py={3}>{formatDateTime(temp.formattedDate || temp.date)}</Td>
                      <Td py={3}>
                        <Badge colorScheme={getTemperatureColor(temp.temperature)} px={2} py={1}>
                          {temp.temperature ? `${temp.temperature}°C` : 'N/A'}
                        </Badge>
                      </Td>
                      <Td py={3}>
                        <Badge colorScheme="green" px={2} py={1}>
                          Normal
                        </Badge>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </TableContainer>
            <PaginationControls />
          </Box>
        );

      case 'heartRateVariabilities':
        // Check if this is Corsano data
        const hasCoranoHRVData = data.some(hrv =>
          hrv.mrr !== undefined || hrv.sdnn !== undefined || hrv.hf !== undefined ||
          hrv.lf !== undefined || hrv.vlf !== undefined || hrv.si !== undefined
        );

        if (hasCoranoHRVData) {
          return (
            <Box>
              <TableContainer>
                <Table variant="simple" size="sm">
                  <Thead bg={tableHeaderBg}>
                    <Tr>
                      <Th fontSize="xs">Time</Th>
                      <Th fontSize="xs">RMSSD</Th>
                      <Th fontSize="xs">PNN50</Th>
                      <Th fontSize="xs">SDNN</Th>
                      <Th fontSize="xs">MRR</Th>
                      <Th fontSize="xs">HF</Th>
                      <Th fontSize="xs">LF</Th>
                      <Th fontSize="xs">SI</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {data.map((hrv) => (
                      <Tr key={hrv.id} _hover={{ bg: tableRowHoverBg }}>
                        <Td py={3}>{formatDateTime(hrv.formattedDate || hrv.date)}</Td>
                        <Td py={3}>
                          <Badge colorScheme="purple" px={2} py={1}>
                            {hrv.rmssd ? hrv.rmssd.toFixed(2) : 'N/A'}
                          </Badge>
                        </Td>
                        <Td py={3}>
                          <Badge colorScheme="teal" px={2} py={1}>
                            {hrv.pnn50 ? hrv.pnn50.toFixed(2) : 'N/A'}
                          </Badge>
                        </Td>
                        <Td py={3}>
                          <Badge colorScheme="blue" px={2} py={1}>
                            {hrv.sdnn ? hrv.sdnn.toFixed(2) : 'N/A'}
                          </Badge>
                        </Td>
                        <Td py={3}>
                          <Badge colorScheme="green" px={2} py={1}>
                            {hrv.mrr ? hrv.mrr.toFixed(2) : 'N/A'}
                          </Badge>
                        </Td>
                        <Td py={3}>
                          <Badge colorScheme="orange" px={2} py={1}>
                            {hrv.hf ? hrv.hf.toFixed(2) : 'N/A'}
                          </Badge>
                        </Td>
                        <Td py={3}>
                          <Badge colorScheme="yellow" px={2} py={1}>
                            {hrv.lf ? hrv.lf.toFixed(2) : 'N/A'}
                          </Badge>
                        </Td>
                        <Td py={3}>
                          <Badge colorScheme="red" px={2} py={1}>
                            {hrv.si ? hrv.si.toFixed(2) : 'N/A'}
                          </Badge>
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </TableContainer>
              <PaginationControls />
            </Box>
          );
        }

        // Regular HRV table
        return (
          <Box>
            <TableContainer>
              <Table variant="simple" size="sm">
                <Thead bg={tableHeaderBg}>
                  <Tr>
                    <Th fontSize="xs">Time</Th>
                    <Th fontSize="xs">RMSSD</Th>
                    <Th fontSize="xs">PNN50</Th>
                    <Th fontSize="xs">Status</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {data.map((hrv) => (
                    <Tr key={hrv.id} _hover={{ bg: tableRowHoverBg }}>
                      <Td py={3}>{formatDateTime(hrv.formattedDate || hrv.date)}</Td>
                      <Td py={3}>
                        <Badge colorScheme="purple" px={2} py={1}>
                          {hrv.rmssd || 'N/A'}
                        </Badge>
                      </Td>
                      <Td py={3}>
                        <Badge colorScheme="teal" px={2} py={1}>
                          {hrv.pnn50 || 'N/A'}
                        </Badge>
                      </Td>
                      <Td py={3}>
                        <Badge colorScheme="green" px={2} py={1}>Normal</Badge>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </TableContainer>
            <PaginationControls />
          </Box>
        );

      case 'emographies':
        // Check if this is Corsano data
        const hasCoranoEmgData = data.some(emg =>
          emg.cz !== undefined || emg.pcz !== undefined || emg.czh !== undefined ||
          emg.cc !== undefined || emg.quality !== undefined || emg.stressSkin !== undefined
        );

        if (hasCoranoEmgData) {
          return (
            <Box>
              <TableContainer>
                <Table variant="simple" size="sm">
                  <Thead bg={tableHeaderBg}>
                    <Tr>
                      <Th fontSize="xs">Time</Th>
                      <Th fontSize="xs">CZ</Th>
                      <Th fontSize="xs">PCZ</Th>
                      <Th fontSize="xs">CZH</Th>
                      <Th fontSize="xs">CC</Th>
                      <Th fontSize="xs">Quality</Th>
                      <Th fontSize="xs">Stress Skin</Th>
                      <Th fontSize="xs">Stress Quality</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {data.map((emg) => (
                      <Tr key={emg.id} _hover={{ bg: tableRowHoverBg }}>
                        <Td py={3}>{formatDateTime(emg.formattedDate || emg.date)}</Td>
                        <Td py={3}>
                          <Badge colorScheme="blue" px={2} py={1}>
                            {emg.cz !== undefined ? emg.cz : 'N/A'}
                          </Badge>
                        </Td>
                        <Td py={3}>
                          <Badge colorScheme="green" px={2} py={1}>
                            {emg.pcz !== undefined ? emg.pcz : 'N/A'}
                          </Badge>
                        </Td>
                        <Td py={3}>
                          <Badge colorScheme="purple" px={2} py={1}>
                            {emg.czh !== undefined ? emg.czh : 'N/A'}
                          </Badge>
                        </Td>
                        <Td py={3}>
                          <Badge colorScheme="orange" px={2} py={1}>
                            {emg.cc !== undefined ? emg.cc : 'N/A'}
                          </Badge>
                        </Td>
                        <Td py={3}>
                          <Badge colorScheme={emg.quality > 2 ? "green" : emg.quality > 0 ? "yellow" : "red"} px={2} py={1}>
                            {emg.quality !== undefined ? emg.quality : 'N/A'}
                          </Badge>
                        </Td>
                        <Td py={3}>
                          <Badge colorScheme="teal" px={2} py={1}>
                            {emg.stressSkin !== undefined ? emg.stressSkin : 'N/A'}
                          </Badge>
                        </Td>
                        <Td py={3}>
                          <Badge colorScheme={emg.stressSkinQuality > 2 ? "green" : emg.stressSkinQuality > 0 ? "yellow" : "red"} px={2} py={1}>
                            {emg.stressSkinQuality !== undefined ? emg.stressSkinQuality : 'N/A'}
                          </Badge>
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </TableContainer>
              <PaginationControls />
            </Box>
          );
        }

        // Regular emographies table
        return (
          <Box>
            <TableContainer>
              <Table variant="simple" size="sm">
                <Thead bg={tableHeaderBg}>
                  <Tr>
                    <Th fontSize="xs">Time</Th>
                    <Th fontSize="xs">EDA Value</Th>
                    <Th fontSize="xs">Status</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {data.map((emg) => (
                    <Tr key={emg.id} _hover={{ bg: tableRowHoverBg }}>
                      <Td py={3}>{formatDateTime(emg.formattedDate || emg.date)}</Td>
                      <Td py={3}>
                        <Badge colorScheme="teal" px={2} py={1}>
                          {emg.eda || emg.value || 'N/A'}
                        </Badge>
                      </Td>
                      <Td py={3}>
                        <Badge colorScheme="green" px={2} py={1}>Normal</Badge>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </TableContainer>
            <PaginationControls />
          </Box>
        );

      case 'stressAcclerometer':
        return (
          <Box>
            <TableContainer>
              <Table variant="simple" size="sm">
                <Thead bg={tableHeaderBg}>
                  <Tr>
                    <Th fontSize="xs">Time</Th>
                    <Th fontSize="xs">X-Axis (g)</Th>
                    <Th fontSize="xs">Y-Axis (g)</Th>
                    <Th fontSize="xs">Z-Axis (g)</Th>
                    <Th fontSize="xs">Magnitude</Th>
                    <Th fontSize="xs">Quality</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {data.map((item) => (
                    <Tr key={item.id} _hover={{ bg: tableRowHoverBg }}>
                      <Td py={3}>{formatDateTime(item.formattedDate || item.date)}</Td>
                      <Td py={3}>
                        <Badge colorScheme="blue" px={2} py={1}>
                          {item.acc_x ? ((item.acc_x / 32768) * 2).toFixed(3) : 'N/A'}
                        </Badge>
                      </Td>
                      <Td py={3}>
                        <Badge colorScheme="green" px={2} py={1}>
                          {item.acc_y ? ((item.acc_y / 32768) * 2).toFixed(3) : 'N/A'}
                        </Badge>
                      </Td>
                      <Td py={3}>
                        <Badge colorScheme="red" px={2} py={1}>
                          {item.acc_z ? ((item.acc_z / 32768) * 2).toFixed(3) : 'N/A'}
                        </Badge>
                      </Td>
                      <Td py={3}>
                        <Badge colorScheme="purple" px={2} py={1}>
                          {item.magnitude ? item.magnitude.toFixed(3) : 'N/A'}
                        </Badge>
                      </Td>
                      <Td py={3}>{item.quality || 'N/A'}</Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </TableContainer>
            <PaginationControls />
          </Box>
        );

      case 'stressAnnotation':
        return (
          <Box>
            <TableContainer>
              <Table variant="simple" size="sm">
                <Thead bg={tableHeaderBg}>
                  <Tr>
                    <Th fontSize="xs">Time</Th>
                    <Th fontSize="xs">Event</Th>
                    <Th fontSize="xs">Quality</Th>
                    <Th fontSize="xs">Comment</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {data.map((item) => (
                    <Tr key={item.id} _hover={{ bg: tableRowHoverBg }}>
                      <Td py={3}>{formatDateTime(item.formattedDate || item.date)}</Td>
                      <Td py={3}>
                        <Badge colorScheme="blue" px={2} py={1}>
                          {item.annotation || 'N/A'}
                        </Badge>
                      </Td>
                      <Td py={3}>{item.quality || 'N/A'}</Td>
                      <Td py={3}>{item.comment || 'N/A'}</Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </TableContainer>
            <PaginationControls />
          </Box>
        );

      case 'stressBioz':
        return (
          <Box>
            <TableContainer>
              <Table variant="simple" size="sm">
                <Thead bg={tableHeaderBg}>
                  <Tr>
                    <Th fontSize="xs">Time</Th>
                    <Th fontSize="xs">BioZ</Th>
                    <Th fontSize="xs">Quality</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {data.map((item) => (
                    <Tr key={item.id} _hover={{ bg: tableRowHoverBg }}>
                      <Td py={3}>{formatDateTime(item.formattedDate || item.date)}</Td>
                      <Td py={3}>
                        <Badge colorScheme="purple" px={2} py={1}>
                          {item.bioz || 'N/A'}
                        </Badge>
                      </Td>
                      <Td py={3}>{item.quality || 'N/A'}</Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </TableContainer>
            <PaginationControls />
          </Box>
        );

      case 'stressHeatFlux':
        return (
          <Box>
            <TableContainer>
              <Table variant="simple" size="sm">
                <Thead bg={tableHeaderBg}>
                  <Tr>
                    <Th fontSize="xs">Time</Th>
                    <Th fontSize="xs">Heat Flux</Th>
                    <Th fontSize="xs">Temperature</Th>
                    <Th fontSize="xs">CBT</Th>
                    <Th fontSize="xs">Quality</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {data.map((item) => (
                    <Tr key={item.id} _hover={{ bg: tableRowHoverBg }}>
                      <Td py={3}>{formatDateTime(item.formattedDate || item.date)}</Td>
                      <Td py={3}>
                        <Badge colorScheme="orange" px={2} py={1}>
                          {item.heatFlux || 'N/A'}
                        </Badge>
                      </Td>
                      <Td py={3}>
                        <Badge colorScheme={getTemperatureColor(item.temperature)} px={2} py={1}>
                          {item.temperature ? `${item.temperature.toFixed(1)}°C` : 'N/A'}
                        </Badge>
                      </Td>
                      <Td py={3}>
                        <Badge colorScheme="red" px={2} py={1}>
                          {item.cbt ? `${item.cbt.toFixed(1)}°C` : 'N/A'}
                        </Badge>
                      </Td>
                      <Td py={3}>{item.quality || 'N/A'}</Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </TableContainer>
            <PaginationControls />
          </Box>
        );

      case 'stressPPG2':
      case 'stressPPG3':
        const isPPG3 = type.key === 'stressPPG3';
        return (
          <Box>
            <TableContainer>
              <Table variant="simple" size="sm">
                <Thead bg={tableHeaderBg}>
                  <Tr>
                    <Th fontSize="xs">Time</Th>
                    <Th fontSize="xs">PPG Value</Th>
                    <Th fontSize="xs">Heart Rate</Th>
                    <Th fontSize="xs">Quality</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {data.map((item) => (
                    <Tr key={item.id} _hover={{ bg: tableRowHoverBg }}>
                      <Td py={3}>{formatDateTime(item.formattedDate || item.date)}</Td>
                      <Td py={3}>
                        <Badge colorScheme="cyan" px={2} py={1}>
                          {isPPG3 ? item.ppg3 : item.ppg2 || 'N/A'}
                        </Badge>
                      </Td>
                      <Td py={3}>
                        <Badge colorScheme={getBPMColor(item.heartRate)} px={2} py={1}>
                          {item.heartRate ? `${item.heartRate} BPM` : 'N/A'}
                        </Badge>
                      </Td>
                      <Td py={3}>{item.quality || 'N/A'}</Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </TableContainer>
            <PaginationControls />
          </Box>
        );

      case 'stressRRInterval':
        return (
          <Box>
            <TableContainer>
              <Table variant="simple" size="sm">
                <Thead bg={tableHeaderBg}>
                  <Tr>
                    <Th fontSize="xs">Time</Th>
                    <Th fontSize="xs">RR Interval</Th>
                    <Th fontSize="xs">Heart Rate</Th>
                    <Th fontSize="xs">HRV</Th>
                    <Th fontSize="xs">Quality</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {data.map((item) => (
                    <Tr key={item.id} _hover={{ bg: tableRowHoverBg }}>
                      <Td py={3}>{formatDateTime(item.formattedDate || item.date)}</Td>
                      <Td py={3}>
                        <Badge colorScheme="blue" px={2} py={1}>
                          {item.rrInterval ? `${item.rrInterval.toFixed(0)} ms` : 'N/A'}
                        </Badge>
                      </Td>
                      <Td py={3}>
                        <Badge colorScheme={getBPMColor(item.heartRate)} px={2} py={1}>
                          {item.heartRate ? `${item.heartRate} BPM` : 'N/A'}
                        </Badge>
                      </Td>
                      <Td py={3}>
                        <Badge colorScheme="purple" px={2} py={1}>
                          {item.hrv ? `${item.hrv.toFixed(1)} ms` : 'N/A'}
                        </Badge>
                      </Td>
                      <Td py={3}>{item.quality || 'N/A'}</Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </TableContainer>
            <PaginationControls />
          </Box>
        );

      case 'stressSelfReport':
        return (
          <Box>
            <TableContainer>
              <Table variant="simple" size="sm">
                <Thead bg={tableHeaderBg}>
                  <Tr>
                    <Th fontSize="xs">Time</Th>
                    <Th fontSize="xs">Stress Level</Th>
                    <Th fontSize="xs">Confidence</Th>
                    <Th fontSize="xs">Comment</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {data.map((item) => (
                    <Tr key={item.id} _hover={{ bg: tableRowHoverBg }}>
                      <Td py={3}>{formatDateTime(item.formattedDate || item.date)}</Td>
                      <Td py={3}>
                        <Badge colorScheme="red" px={2} py={1}>
                          {item.stressLevel || 'N/A'}
                        </Badge>
                      </Td>
                      <Td py={3}>
                        <Badge colorScheme="green" px={2} py={1}>
                          {item.confidence ? `${(item.confidence * 100).toFixed(1)}%` : 'N/A'}
                        </Badge>
                      </Td>
                      <Td py={3}>{item.comment || 'N/A'}</Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </TableContainer>
            <PaginationControls />
          </Box>
        );

      default:
        // Generic table for stress measurements and other types
        return (
          <Box>
            <TableContainer>
              <Table variant="simple" size="sm">
                <Thead bg={tableHeaderBg}>
                  <Tr>
                    <Th fontSize="xs">Time</Th>
                    <Th fontSize="xs">Value</Th>
                    <Th fontSize="xs">Type</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {data.map((item, index) => (
                    <Tr key={item.id || index} _hover={{ bg: tableRowHoverBg }}>
                      <Td py={3}>{formatDateTime(item.date)}</Td>
                      <Td py={3}>
                        <Badge colorScheme="gray" px={2} py={1}>
                          {item.value || item.data || 'N/A'}
                        </Badge>
                      </Td>
                      <Td py={3}>{type.label}</Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </TableContainer>
            <PaginationControls />
          </Box>
        );
    }
  }
};

export default PatientDetails; 
